/**
 * TekStak's account-board widget registry — the type → component map for the
 * shared @techstack/ui AccountBoard, on /accounts.
 *
 * Every widget here mounts the SAME component the equivalent tab renders. The
 * board changed where a panel sits and who decides, not what it is: the Dash
 * panels come through `AccountsDashTab` / `AccountsOverviewIntel`'s `section`
 * prop (so their drill wiring stays in one place), and the deeper tabs mount
 * whole. No panel is reimplemented for the board.
 *
 * Instances share data through react-query, not through prop drilling: five
 * widgets mounting `AccountsDashTab` all hit the same query keys, so they cost
 * one fetch each rather than five.
 *
 * A type absent from this map is not an error — the board renders a "not
 * available in this app" placeholder, which is how a board arranged in GAS
 * (solicitations, meeting notes, deal signals) survives being opened here.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  WidgetStatus, RenewalRunway, ShareOfWallet, CountdownRing, countdownTone,
  CoverageRing, coverageState, materialityFloor, isOpenCoverage, COVERAGE_STATE_LABEL,
  BuyingRhythm, securityCategoryColor, DisplacementBump,
  VendorCategoryHeatmap, TileFigure, WidgetTileStub,
  PartnerInvolvement,
} from '@techstack/ui';
import { getFiscalCalendar, fiscalYearRange, DEFAULT_FISCAL_CALENDAR_KEY, ACCOUNT_WIDGET_META } from '@techstack/core';
import { useFiscalBasisContext } from '../../../contexts/FiscalBasisContext';
// The board's global time filter (frontend invariant 7) — the partners tile
// binds the same window its panel does, so the two share a cache entry.
import { useTimeFilterContext } from '../../../contexts/TimeFilterContext';
import apiClient from '../../../lib/apiClient';
import { fmtMoney, accountKey, footprintWindow, fiscalSpanLabel, clipRangeToWindow } from '../accountsFormat';
import { sortRenewals } from '../renewals';
import PoDetailModal from '../../PoDetailModal/PoDetailModal';
import SharedTransactionsDrilldownModal from '../../shared/SharedTransactionsDrilldownModal';
import AccountsDashTab from '../AccountsDashTab';
import AccountsOverviewIntel from '../AccountsOverviewIntel';
import { AccountsPeopleTab, useWorkspaceData } from '../AccountsPeopleTabs';
import AccountsContactsDirectory from '../AccountsContactsDirectory';
import AccountsCompetitiveTab from '../AccountsCompetitiveTab';
import AccountsMeetingsTab from '../AccountsMeetingsTab';
import AccountsBcpsTab from '../AccountsBcpsTab';
import AccountsPyramidTab from '../AccountsPyramidTab';
import AccountsTrapsTab from '../AccountsTrapsTab';
import AccountsTechmapTab from '../AccountsTechmapTab';
import AccountsLocationsTab from '../AccountsLocationsTab';
import AccountNewsSection, { useAccountNews } from '../../AccountWorkspace/AccountNewsSection';
import AccountNotesSection from '../../AccountWorkspace/AccountNotesSection';
import ContactInbox from '../../AccountWorkspace/ContactInbox';
import ContactDetailModal from '../../AccountWorkspace/ContactDetailModal';
import AccountHealthScore from '../../AccountWorkspace/AccountHealthScore';
import ProactiveQuestions from '../../AccountWorkspace/ProactiveQuestions';
import CommitmentTracker from '../../AccountWorkspace/CommitmentTracker';
import ZeroTrust from '../../ZeroTrust';
import { useBoardAccount } from './BoardContext';
import './widgets.css';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------


function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - Date.now()) / 86400000);
}

/**
 * The `/api/workspace` aggregation — AccountsPeopleTabs' own hook, not a copy.
 * Same query key the org chart, contacts and meetings tabs use, so the payload
 * is fetched once and shared however many widgets want a slice of it, and a
 * mutation on any of them invalidates all of them.
 *
 * Only the widgets that genuinely need it call this. Most of the board reads
 * the account-scoped endpoints its panels already own.
 */
function useWorkspace() {
  const { selectedDepartment } = useBoardAccount();
  return useWorkspaceData(selectedDepartment);
}

/** Wraps a tab component that takes `{ selectedDepartment }` and nothing else. */
/** A tab mounted with fixed props — used where two widgets share a component
 *  but must not render the same thing (org chart vs contact list). */
const fromTabWith = (Component, extra) => function FixedTabWidget() {
  const { selectedDepartment } = useBoardAccount();
  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  return (
    <div className="acb-pad">
      <Component selectedDepartment={selectedDepartment} compact {...extra} />
    </div>
  );
};

const fromTab = (Component) => function TabWidget() {
  const { selectedDepartment } = useBoardAccount();
  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  // `compact` is the board's contract with a panel: the shell owns the title
  // and the way into the full view, so don't render a second set. Panels that
  // don't accept it simply ignore it.
  return (
    <div className="acb-pad">
      <Component selectedDepartment={selectedDepartment} compact />
    </div>
  );
};

/**
 * Wraps one `section` of the Dash tab, keeping its data + drill wiring intact.
 *
 * `fill` comes from the board (which reads it off the catalog) rather than
 * being inferred here, so the flag can never drift from the widget's meta and
 * a non-board page mounting the same section gets the paged, fixed-height
 * treatment it wants.
 */
const fromDash = (section) => function DashWidget({ fill }) {
  const { selectedDepartment } = useBoardAccount();
  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  return (
    <AccountsDashTab selectedDepartment={selectedDepartment} section={section} fill={fill} />
  );
};

/**
 * Wraps one `section` of the overview intel stack.
 *
 * `onOpen` is the board's own "open the full view" callback — the same one
 * behind the header arrow. Forwarding it lets a panel put that target on its
 * own controls too, so a truncated list can offer "Show N more" instead of
 * stating the remainder and leaving the reader to find it.
 */
const fromIntel = (section) => function IntelWidget({ onOpen }) {
  const { selectedDepartment } = useBoardAccount();
  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  return (
    <div className="acb-pad">
      <AccountsOverviewIntel
        selectedDepartment={selectedDepartment}
        section={section}
        onOpen={onOpen}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// widgets that are board-local (no existing panel renders exactly this)
// ---------------------------------------------------------------------------

function ContactInboxWidget() {
  return <div className="acb-pad"><ContactInbox /></div>;
}

/**
 * Open tasks — a list you can act on, not a wall of text.
 *
 * Ryan: "they should have completion check boxes, maybe colored line that
 * indicates their priority, not just a list of plain text so crowded not
 * elegant font feels too bold and doesn't fit the vibe of the page."
 *
 * Three things were wrong and they compounded. Every row was semibold at the
 * same size, so ten rows arrived as one grey block with no way in. `priority`
 * was on every task — urgent / high / medium / low, all four present in real
 * data — and the widget threw it away, so the loudest-looking row was
 * whichever had the longest title. And the list was READ-ONLY on a board whose
 * other widgets all do something: the one action a task list owes you is
 * finishing a task.
 *
 * So: a checkbox that completes (optimistically, the house standard — patch
 * local state, persist behind, roll back on failure), a 2px priority rail
 * carrying the one field that ranks the list, and regular weight with the due
 * date demoted to a caption. The title stops competing with the whole page.
 */
/**
 * How many of a list's items fit in a clipped box — found by REDUCING, never by
 * dividing.
 *
 * Dividing the box height by a row height is what the purchase history does,
 * and it works there because its rows are uniform. Tasks rows are not (a due
 * date adds a line) and news cards are wildly not — five real stories measured
 * 236 / 150 / 130 / 130 / 150. There is no row height to divide by, so this
 * looks instead: while the content overflows, drop one.
 *
 * THE REDUCTION IS MONOTONE, and that is the whole safety argument. Each pass
 * only ever removes an item, so it converges in at most `total` steps and
 * cannot oscillate. That is the failure the board-level fit could never rule
 * out: there, a measurement changed a height that changed the measurement. Here
 * the box is a CLIPPED child of a cell the grid has already sized, so dropping
 * an item cannot change the height being measured — the loop has no return path
 * by construction, not by tuning.
 *
 * The count resets when the data or the box size changes, which is exactly when
 * the previous answer stopped describing anything real.
 */
function useFitCount(ref, total, resetKey) {
  const [count, setCount] = useState(total);
  const lastH = useRef(-1);
  // The smallest count that has been SEEN to overflow. Everything at or above
  // it is known bad, so the search settles at `tooMany - 1` and stays there.
  const tooMany = useRef(Infinity);

  const reset = () => { lastH.current = -1; tooMany.current = Infinity; setCount(total); };
  useEffect(reset, [total, resetKey]);

  /*
   * Deliberately no dependency array: each step produces a render and the next
   * step has to run against the layout that render produced.
   *
   * It TERMINATES because the two moves are asymmetric. Overflowing records a
   * ceiling and steps down; fitting steps up only while strictly below that
   * ceiling. So a count is never tried twice after it has overflowed once, and
   * the search lands on `tooMany - 1`.
   *
   * Pure reduction was the first attempt and it over-shot to a single story:
   * news cards are taller before their images settle, so the early passes
   * measured content that no longer existed, and with no way back it stayed
   * there. The ceiling is what makes recovering from that safe — re-expansion
   * cannot re-enter a size already known to overflow, so it cannot oscillate.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.clientHeight;
    if (h <= 0) return;
    if (Math.abs(h - lastH.current) >= 2) {
      // A different box is a different question; forget what was known.
      lastH.current = h;
      tooMany.current = Infinity;
      setCount(total);
    }
    if (el.scrollHeight > h + 2) {
      setCount((n) => { tooMany.current = Math.min(tooMany.current, n); return n > 1 ? n - 1 : n; });
    } else {
      setCount((n) => (n < total && n + 1 < tooMany.current ? n + 1 : n));
    }
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    /*
     * ONE re-run once the content has stopped settling.
     *
     * The ceiling is recorded from whatever was on screen at the time, and a
     * news card is taller before its image and fonts land — so the first pass
     * can condemn a count that would actually fit and, because a ceiling is
     * never revisited, stay one story short for good. Asking again after the
     * page has quietened re-opens exactly that question.
     *
     * A single shot, not an interval: observing the CONTENT for changes would
     * be the general fix and is also a loop, because the search changes the
     * content it would be observing.
     */
    const settle = setTimeout(reset, 1200);

    if (typeof ResizeObserver === 'undefined') return () => clearTimeout(settle);
    const ro = new ResizeObserver(() => {
      const el2 = ref.current;
      if (el2 && Math.abs(el2.clientHeight - lastH.current) >= 2) reset();
    });
    ro.observe(el);
    return () => { clearTimeout(settle); ro.disconnect(); };
  }, [ref, total]);

  return count;
}

function TasksWidget({ config, size }) {
  const q = useWorkspace();
  const { selectedDepartment } = useBoardAccount();
  const queryClient = useQueryClient();
  const qKey = ['workspace', selectedDepartment?.department_id,
    selectedDepartment?.acronym || selectedDepartment?.official_name || null];

  // Which task is open for editing. Inline rather than a modal: "make
  // adjustments right from the widget" (Ryan) means the list should not go
  // away to change one field on it.
  const [openId, setOpenId] = useState(null);

  const patch = useMutation({
    mutationFn: ({ id, ...fields }) => apiClient.patch(`/api/v1/tasks/${id}`, fields),
    onMutate: async ({ id, ...fields }) => {
      await queryClient.cancelQueries({ queryKey: qKey });
      const prev = queryClient.getQueryData(qKey);
      queryClient.setQueryData(qKey, (old) => (old ? {
        ...old,
        tasks: (old.tasks || []).map((t) => (t.id === id ? { ...t, ...fields } : t)),
      } : old));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) queryClient.setQueryData(qKey, ctx.prev); },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qKey }),
  });

  const complete = useMutation({
    mutationFn: (id) => apiClient.post(`/api/v1/tasks/${id}/complete`, {}),
    // The row leaves the list on the press, not on the round trip. A checkbox
    // that waits on a network hop feels broken even when it works.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: qKey });
      const prev = queryClient.getQueryData(qKey);
      queryClient.setQueryData(qKey, (old) => (old ? {
        ...old,
        tasks: (old.tasks || []).filter((t) => t.id !== id),
      } : old));
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      // Put it back. A task that silently fails to complete is worse than one
      // that never moved.
      if (ctx?.prev) queryClient.setQueryData(qKey, ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qKey }),
  });

  const limit = Number(config?.limit) > 0 ? Number(config.limit) : 10;

  /*
   * RENDER WHAT FITS AND STATE THE REST — the same shape the purchase history
   * uses, and for the same reason: "scrolling in the widget is never the
   * answer. resizing rearranging reconfiguring that is the route we need to
   * go" (Ryan, twice).
   *
   * This widget drew all ten rows whatever the cell held — 737px of list in a
   * 284px body, so it SCROLLED, and a wheel over it moved the list instead of
   * the page.
   *
   * Measuring here is safe in the one way that matters: rendering fewer rows
   * cannot change the height of the box being measured. `.acb-tasks-fit` is a
   * clipped child of a cell the grid has already sized, so there is no path
   * from the measurement back into itself. That is precisely what the board
   * -level fit could not promise, and why this lives in the widget.
   */
  const fitRef = useRef(null);

  const allTasks = q.data?.tasks || [];
  const fitCount = useFitCount(fitRef, Math.min(allTasks.length, limit), selectedDepartment?.department_id);

  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) return <WidgetStatus state="error" onRetry={() => q.refetch()} />;
  const all = allTasks;
  const capped = all.slice(0, limit);
  const tasks = capped.slice(0, fitCount);
  const hidden = capped.length - tasks.length;
  if (!capped.length) return <WidgetStatus state="empty" message="No open tasks." />;
  if (size === 'tile') {
    // Overdue, not open. "14 tasks" is a fact about the list; "3 overdue" is
    // the one a rep would stop for, and at this size there is room for one.
    const overdue = all.filter((t) => daysUntil(t.due_date) < 0).length;
    return overdue > 0
      ? <TileFigure value={overdue} label="Overdue" sub={`of ${all.length} open`} tone="crit" />
      : <TileFigure value={all.length} label="Open tasks" />;
  }
  return (
    <div className="acb-tasks-wrap">
      <div className="acb-tasks-fit" ref={fitRef}>
    <ul className="acb-tasks">
      {tasks.map((t) => {
        const d = daysUntil(t.due_date);
        const pr = String(t.priority || 'medium').toLowerCase();
        const open = openId === t.id;
        return (
          <li className={`acb-task acb-task--${pr}${open ? ' is-open' : ''}`} key={t.id}>
            <div className="acb-task__row">
              <input
                type="checkbox"
                className="acb-task__box"
                checked={false}
                disabled={complete.isPending}
                onChange={() => complete.mutate(t.id)}
                aria-label={`Complete: ${t.title}`}
              />
              {/* The title is the disclosure. A whole-row click would fight
                  the checkbox sitting inside it. */}
              <button
                type="button"
                className="acb-task__body"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : t.id)}
              >
                <span className="acb-task__title" title={t.title}>{t.title}</span>
                <span className="acb-task__meta">
                  <span className="acb-task__pr">{pr}</span>
                  {t.due_date && (
                    <span className={`acb-task__due${d < 0 ? ' is-overdue' : d <= 2 ? ' is-soon' : ''}`}>
                      {d < 0 ? `${-d}d overdue` : d === 0 ? 'due today' : `in ${d}d`}
                    </span>
                  )}
                </span>
              </button>
            </div>

            {open && (
              <div className="acb-task__edit">
                <div className="acb-task__field">
                  <span className="acb-task__label">Priority</span>
                  <div className="acb-task__prs">
                    {['urgent', 'high', 'medium', 'low'].map((level) => (
                      <button
                        key={level}
                        type="button"
                        className={`acb-task__prbtn acb-task__prbtn--${level}${
                          pr === level ? ' is-on' : ''}`}
                        aria-pressed={pr === level}
                        onClick={() => patch.mutate({ id: t.id, priority: level })}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="acb-task__field">
                  <span className="acb-task__label">Due</span>
                  <input
                    type="date"
                    className="acb-task__date"
                    value={t.due_date ? String(t.due_date).slice(0, 10) : ''}
                    onChange={(e) => patch.mutate({ id: t.id, due_date: e.target.value || null })}
                  />
                </label>
              </div>
            )}
          </li>
        );
      })}
    </ul>
      </div>
      {/* The remainder is STATED, never silently dropped: a list that shows
          four of fourteen and says nothing reads as an account with four open
          tasks. */}
      {hidden > 0 && (
        <p className="acb-tasks-more">
          {`${hidden} more ${hidden === 1 ? 'task' : 'tasks'}`}
          {' — make this widget taller, or open the full view'}
        </p>
      )}
    </div>
  );
}

function OpportunitiesWidget({ size }) {
  const q = useWorkspace();
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) return <WidgetStatus state="error" onRetry={() => q.refetch()} />;
  const opportunities = q.data?.opportunities || [];
  if (!opportunities.length) return <WidgetStatus state="empty" message="No open opportunities." />;
  if (size === 'tile') {
    const total = opportunities.reduce((sum, o) => sum + (Number(o.value) || 0), 0);
    return (
      <TileFigure
        value={total > 0 ? fmtMoney(total) : opportunities.length}
        label={total > 0 ? 'Pipeline' : 'Open'}
        sub={total > 0 ? `${opportunities.length} open` : undefined}
        tone="accent"
      />
    );
  }
  return (
    <div className="acb-list">
      {opportunities.map((o) => (
        <div className="acb-row" key={o.id}>
          <span className="acb-row-head">
            <span className="acb-row-title" title={o.name}>{o.name}</span>
            <span className="acb-row-value">{o.value ? fmtMoney(o.value) : '—'}</span>
          </span>
          <span className="acb-row-meta">
            <span className="acb-chip">{o.stage}</span>
            {o.source === 'ai-derived' && <span className="acb-chip">auto-detected</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function MeetingsWidget({ size }) {
  const q = useWorkspace();
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) return <WidgetStatus state="error" onRetry={() => q.refetch()} />;
  const meetings = q.data?.meetings || [];
  if (!meetings.length) return <WidgetStatus state="empty" message="No meetings recorded." />;
  const upcoming = meetings.filter((m) => m.status === 'planned');
  const past = meetings.filter((m) => m.status !== 'planned');
  if (size === 'tile') {
    // The next one if there is one, otherwise how long it has been — which is
    // the more useful number when the answer is "nothing booked".
    const next = upcoming
      .filter((m) => m.meeting_at)
      .sort((a, b) => new Date(a.meeting_at) - new Date(b.meeting_at))[0];
    if (next) {
      const d = daysUntil(next.meeting_at);
      return (
        <TileFigure
          value={d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : `${d}d`}
          label="Until next"
          sub={fmtDate(next.meeting_at)}
          tone={d <= 2 ? 'accent' : 'neutral'}
        />
      );
    }
    const last = past
      .filter((m) => m.meeting_at)
      .sort((a, b) => new Date(b.meeting_at) - new Date(a.meeting_at))[0];
    return last
      ? <TileFigure value={`${-daysUntil(last.meeting_at)}d`} label="Since last met" tone="warn" />
      : <TileFigure value={meetings.length} label="Meetings" />;
  }
  const row = (m) => (
    <div className="acb-row" key={m.meeting_id}>
      <span className="acb-row-head">
        <span className="acb-row-title" title={m.title}>{m.title}</span>
        {m.meeting_at && <span className="acb-row-dim">{fmtDate(m.meeting_at)}</span>}
      </span>
    </div>
  );
  return (
    <div className="acb-list">
      {upcoming.length > 0 && <div className="acb-subhead">Upcoming</div>}
      {upcoming.slice(0, 5).map(row)}
      {past.length > 0 && <div className="acb-subhead">Recent</div>}
      {past.slice(0, 5).map(row)}
    </div>
  );
}

/**
 * Widgets keyed on the CRM `accounts` row rather than the department.
 *
 * `/api/workspace` already resolves and returns that id, which is what makes
 * these placeable here at all — ACCOUNTS-GAS-PARITY listed the missing
 * account-id resolution as the blocker for Meeting Notes on this page.
 */
const withAccountId = (Component, label) => function AccountIdWidget() {
  const q = useWorkspace();
  const { deptKey } = useBoardAccount();
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) return <WidgetStatus state="error" onRetry={() => q.refetch()} />;
  const accountId = q.data?.account_id;
  if (!accountId) {
    return (
      <WidgetStatus
        state="empty"
        message={`No CRM account row for this agency yet, so ${label} has nothing to key on.`}
      />
    );
  }
  return <div className="acb-pad"><Component accountId={accountId} accountName={deptKey} /></div>;
};

function NewsWidget() {
  const { selectedDepartment, deptKey } = useBoardAccount();
  const account = selectedDepartment?.acronym || deptKey;
  const workspace = useWorkspace();
  const [contact, setContact] = useState(null);

  // `news_item_contacts.contact_id` joins `contacts.id`, which is the same id
  // the workspace payload already carries — so the chip resolves to a full
  // contact out of the cache the People widget has already filled, and opens
  // the same detail modal the org chart does. No second fetch, no half-target
  // like /contacts?contact=… which that page does not read.
  // Memoised: `?? []` mints a fresh array every render, which would change the
  // callback's dependency every render and quietly defeat the memo.
  const contacts = useMemo(() => workspace.data?.contacts || [], [workspace.data]);
  const onContactClick = useCallback((c) => {
    const found = contacts.find((x) => String(x.id) === String(c?.contact_id));
    if (found) setContact(found);
  }, [contacts]);

  /*
   * DRAW THE STORIES THAT FIT AND SAY HOW MANY ARE LEFT.
   *
   * Five stories is ~899px of card in a 662px body, so this scrolled. News
   * items are not a uniform height — five real ones measured 236 / 150 / 130 /
   * 130 / 150 — so there is no row height to divide by; the only honest way to
   * know how many fit is to look.
   *
   * THE REDUCTION IS MONOTONE, and that is what makes it safe. Each pass only
   * ever removes a story, never adds one back, so it converges in at most five
   * steps and cannot oscillate — the failure mode that sank the board-level
   * fit, where a measurement fed a decision that changed the measurement. The
   * count resets only when the account or the box size changes, which is
   * exactly when the old answer stopped being about anything real.
   */
  const fitRef = useRef(null);

  const newsQ = useAccountNews(account, NEWS_LIMIT);
  const totalStories = newsQ.data?.items?.length ?? 0;
  const shown = useFitCount(fitRef, totalStories, account);
  const hiddenStories = Math.max(0, totalStories - shown);

  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  return (
    <div className="acb-news-wrap">
      <div className="acb-news-fit" ref={fitRef}>
    <div className="acb-pad">
      <AccountNewsSection
        account={account}
        limit={NEWS_LIMIT}
        maxItems={shown}
        // Only offer the affordance when it can actually resolve. Before the
        // workspace lands, or for someone tagged at another agency, the chip
        // stays plain text rather than a button that misses.
        onContactClick={contacts.length > 0 ? onContactClick : undefined}
      />
      {contact && (
        <ContactDetailModal
          contact={contact}
          allContacts={contacts}
          onClose={() => setContact(null)}
          onUpdate={() => workspace.refetch?.()}
        />
      )}
    </div>
      </div>
      {hiddenStories > 0 && (
        <p className="acb-news-more">
          {`${hiddenStories} more ${hiddenStories === 1 ? 'story' : 'stories'}`}
          {' — make this widget taller, or open the full view'}
        </p>
      )}
    </div>
  );
}

function NotesWidget() {
  const { selectedDepartment } = useBoardAccount();
  const account = selectedDepartment?.acronym
    || (selectedDepartment?.department_id ? String(selectedDepartment.department_id) : null);
  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  return <div className="acb-pad"><AccountNotesSection account={account} compact limit={4} /></div>;
}

function ZeroTrustWidget() {
  const { selectedDepartment, departments } = useBoardAccount();
  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  return (
    <div className="acb-pad">
      <ZeroTrust selectedDepartment={selectedDepartment} departments={departments} />
    </div>
  );
}

/**
 * The drawn money widgets (W4).
 *
 * These two are the only board-local widgets that are not a window onto an
 * existing panel — they are a different READING of data the list-shaped
 * widgets already fetch, so they reuse those query keys exactly and cost
 * nothing extra when both are on the board.
 *
 * Charts live in @techstack/ui (frontend invariant 5); everything here is the
 * account plumbing around them: which query, and where a click goes.
 */

/** The renewals query, keyed identically to the list widget and the tab. */
function useRenewals() {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  return useQuery({
    queryKey: ['accounts-renewals', account],
    queryFn: () => apiClient.get('/api/v1/renewals', { params: { account, months: 24 } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });
}

function RenewalRunwayWidget({ fill, size }) {
  const { selectedDepartment } = useBoardAccount();
  const q = useRenewals();
  const [po, setPo] = useState(null);

  const onPick = useCallback((rn) => {
    const first = Array.isArray(rn?.poNumbers) ? rn.poNumbers.find(Boolean) : null;
    if (first) setPo(first);
  }, []);

  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    return (
      <WidgetStatus
        state="error"
        message={`Renewals unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }
  const renewals = sortRenewals(q.data?.renewals || []);
  if (renewals.length === 0) {
    return (
      <WidgetStatus state="empty" message="Nothing expires at this agency in the next 24 months." />
    );
  }

  if (size === 'tile') {
    // Days to the soonest expiry. A count of renewals says how busy the year
    // is; the clock says whether to do something this week.
    const next = renewals.filter((r) => r.endDate)[0];
    const d = next ? daysUntil(next.endDate) : null;
    return d == null
      ? <TileFigure value={renewals.length} label="Renewals" sub="next 24 months" />
      : (
        <TileFigure
          value={d <= 0 ? 'Now' : `${d}d`}
          label="Next expiry"
          sub={`${renewals.length} in 24mo`}
          tone={d <= 30 ? 'crit' : d <= 90 ? 'warn' : 'neutral'}
        />
      );
  }

  return (
    <div className="acb-chart">
      <RenewalRunway
        renewals={renewals}
        fill={fill}
        months={24}
        onPick={onPick}
      />
      {po && (
        <PoDetailModal
          documentNumber={po}
          departmentId={selectedDepartment?.department_id}
          isOpen
          onClose={() => setPo(null)}
        />
      )}
    </div>
  );
}

/**
 * Share of wallet — against the VENDORS, not the resellers.
 *
 * Ryan: "This is still ugly and stupid it's not granular enough and it doesn't
 * look visually appealing at all." Every tile in it was a reseller — SHI,
 * Crayon, Dell Marketing, Level 3, Rackspace — because it read
 * /api/v1/procurement/summary, which ranks SUPPLIERS: the company that
 * invoiced the agency, not the company that won the business. For a Palo Alto
 * rep, "share of wallet" against SHI answers a question nobody asked.
 *
 * That premise was also why it could not be made to look good. Reseller names
 * are long, so the labels collided; there is no us-and-them among resellers,
 * so every tile was the same grey; and the real vendors were hidden INSIDE
 * those resellers, so the residual held more than the largest named tile. The
 * visual complaints were symptoms of the data being wrong.
 *
 * So it now reads the same endpoint the gaining-and-losing chart does —
 * classified vendors, which is the OEM behind the paper. At CDT that is
 * Microsoft, American Dark Fiber, Lumen, CA Technologies, Cisco, IBM and Palo
 * Alto Networks, with ours pinned in whether or not it ranks. Real
 * competitors, shorter names, and one saturated tile that is us.
 *
 * All technology spend, not security only (Ryan's call): it is the true
 * wallet, and it puts us against Microsoft and Oracle rather than against a
 * rounding error.
 *
 * The leftover tile is named `unattributed`, not "everyone else" — it is spend
 * no vendor could be attributed to, and calling it competitors would invent
 * rivals that are not there.
 */
function ShareOfWalletWidget({ fill, size }) {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;
  const { years } = useTimeFilterContext();
  const [drill, setDrill] = useState(null);

  const q = useQuery({
    // Same shape as every other agency-scoped query on this board: the window
    // is the page's, and it lives in the key so a flip refetches.
    queryKey: ['accounts-wallet-vendors', account, years],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/vendor-trajectory`,
      { params: { ...(years ? { years } : {}), topN: 12 } },
    ),
    enabled: !!account,
    staleTime: 15 * 60 * 1000,
  });

  // The endpoint returns a vendor-per-YEAR series; a wallet wants one figure
  // per vendor across the window.
  const { vendors, total, ours } = useMemo(() => {
    const d = q.data;
    if (!d) return { vendors: [], total: 0, ours: 0 };
    const byVendor = new Map();
    for (const p of d.points || []) {
      byVendor.set(p.vendor, (byVendor.get(p.vendor) || 0) + (Number(p.spend) || 0));
    }
    const rows = [...byVendor.entries()]
      .map(([vendor, spend]) => ({ vendor, spend }))
      .sort((a, b) => b.spend - a.spend);
    const named = rows.reduce((sum, r) => sum + r.spend, 0);
    // Unattributed is REAL money at this account and frequently larger than
    // anything named — $208M against $2.06B at CDT. Excluding it would
    // overstate every share on the chart.
    const grand = named + (Number(d.unattributed) || 0);
    const mine = rows
      .filter((r) => /palo alto/i.test(r.vendor))
      .reduce((sum, r) => sum + r.spend, 0);
    return { vendors: rows, total: grand, ours: mine };
  }, [q.data]);

  const onPick = useCallback((slice) => {
    if (!departmentId || !slice?.name) return;
    setDrill({
      title: `${slice.name} · ${years ? `last ${years}y` : 'all time'}`,
      contextLabel: `${account} · ${years ? `last ${years} years` : 'all time'}`,
      expected: slice.value,
      queryKey: ['accounts-drill-vendor', departmentId, slice.name, years],
      path: `/api/vendor-analysis/${encodeURIComponent(slice.name)}/transactions`,
      params: { department: departmentId, ...(years ? { years } : {}), limit: 500 },
    });
  }, [departmentId, account, years]);

  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    return (
      <WidgetStatus
        state="error"
        message={`Vendor spend unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }
  if (!vendors.length) {
    return <WidgetStatus state="empty" message="No classified vendor spend for this agency." />;
  }

  if (size === 'tile') {
    return total > 0 && ours > 0
      ? (
        <TileFigure
          value={`${(ours / total * 100).toFixed(ours / total < 0.1 ? 1 : 0)}%`}
          label="Our share"
          sub={fmtMoney(total)}
          tone="accent"
        />
      )
      : <TileFigure value={fmtMoney(total)} label="Vendor spend" sub="none of it ours" />;
  }

  return (
    <div className="acb-chart">
      {/*
        * OUR SHARE, IN WORDS, BECAUSE THE TILE CANNOT CARRY IT.
        *
        * Area encodes spend, and at CDT ours is $11.9M of a $2.06B wallet —
        * 0.6%. The tile measures 26x20px. That is the chart being HONEST, and
        * distorting the area to flatter us would be the one change that makes
        * this widget lie.
        *
        * But "how big are we here" is the question it exists to answer, and a
        * 26px speck does not answer it. So the figure is stated in text, where
        * a small number can still be read, and the treemap keeps its job:
        * showing the shape of the wallet and who owns it.
        */}
      <p className="acb-wallet-share">
        {ours > 0 ? (
          <>
            <strong>{fmtMoney(ours)}</strong>
            {' ours · '}
            <span className="acb-wallet-pct">
              {(ours / total * 100).toFixed(ours / total < 0.1 ? 1 : 0)}%
            </span>
            {' of '}
            {fmtMoney(total)}
          </>
        ) : (
          <>No Palo Alto spend recorded · {fmtMoney(total)} total</>
        )}
      </p>
      <ShareOfWallet
        vendors={vendors}
        totalSpend={total}
        topN={10}
        residualLabel="Unattributed"
        fill={fill}
        onPick={onPick}
      />
      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        queryKey={drill?.queryKey || ['accounts-wallet-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null
          ? `Chart figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.`
          : null}
      />
    </div>
  );
}

/**
 * Solicitation clocks (W5) — "what is out for bid, and how long have I got".
 *
 * The Solicitations widget mounted the tab's list: title, id, a status chip and
 * a date you had to subtract from today. As cards with a countdown ring the
 * ring's COLOUR is the whole read, and ten of them sort themselves before a
 * number is looked at.
 *
 * Only fields the list endpoint actually returns are on the card. Checked
 * against production before designing it, which killed two things an earlier
 * sketch had: `estimated_value_usd` is on the table but not in the list SELECT
 * (and not surfaced by the MCP tool either), and `published_date` came back
 * null on every open row — so no dollar figure and no "posted N days ago".
 * `status_raw` is "Posted" on essentially everything, so it is not a chip
 * either; a chip that always says the same word is furniture.
 */

const VEHICLE = /\b(RFP|RFQ|RFI|IFB|RFO|LPA|NCB)\b/i;

/**
 * The clock's four tones, mapped onto the tile's three-plus-neutral.
 *
 * Written out rather than inlined because the two vocabularies are genuinely
 * different: a countdown ring distinguishes "soon" from "ok", a tile has no
 * room to and does not need to.
 */
/** The one vendor this board is sold against. Used wherever a request names it. */
const PANW = 'Palo Alto Networks';

const TILE_TONE_BY_COUNTDOWN = {
  urgent: 'crit',
  soon: 'warn',
  ok: 'accent',
  past: 'neutral',
};

function SolicitationClocksWidget({ size }) {
  const { selectedDepartment } = useBoardAccount();
  const navigate = useNavigate();
  const account = accountKey(selectedDepartment);

  // Same key and params as the tab, so opening the full view is a cache hit.
  const q = useQuery({
    queryKey: ['accounts-solicitations', account],
    queryFn: () => apiClient.get('/api/solicitations', { params: { dept: account, limit: 200 } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    return (
      <WidgetStatus
        state="error"
        message={`Solicitations unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }

  const rows = q.data?.rows || [];
  const open = rows
    .filter((r) => r.close_date && daysUntil(r.close_date) >= 0)
    .sort((a, b) => new Date(a.close_date) - new Date(b.close_date));

  if (open.length === 0) {
    return (
      <WidgetStatus
        state="empty"
        message={rows.length > 0
          ? 'Nothing open right now — recently closed ones are on the Solicitations tab.'
          : 'No Cal eProcure solicitations recorded for this agency.'}
      />
    );
  }

  if (size === 'tile') {
    const soonest = daysUntil(open[0].close_date);
    return (
      <TileFigure
        value={open.length}
        label={open.length === 1 ? 'Open bid' : 'Open bids'}
        sub={`next closes in ${soonest}d`}
        tone={TILE_TONE_BY_COUNTDOWN[countdownTone(soonest)] || 'accent'}
      />
    );
  }

  return (
    <div className="acb-clocks">
      {open.slice(0, 12).map((r) => {
        const d = daysUntil(r.close_date);
        const vehicle = (String(r.title || '').match(VEHICLE) || [])[0];
        return (
          <button
            type="button"
            key={r.solicitation_id}
            className={`acb-clock acb-clock--${countdownTone(d)}`}
            onClick={() => navigate(`/solicitations/${encodeURIComponent(r.solicitation_id)}`)}
            title={`${r.title || 'Untitled'} — closes ${fmtDate(r.close_date)}`}
          >
            <CountdownRing
              days={d}
              label={`Closes in ${d} days`}
            />
            <span className="acb-clock-body">
              <span className="acb-clock-title">{r.title || '(untitled)'}</span>
              <span className="acb-clock-meta">
                {vehicle && <span className="acb-chip">{vehicle.toUpperCase()}</span>}
                <span className="acb-clock-id">{r.solicitation_id}</span>
                {Number(r.sku_count) > 0 && (
                  <span className="acb-clock-dim">{r.sku_count} SKUs</span>
                )}
                {Number(r.attachment_count) > 0 && (
                  <span className="acb-clock-dim">{r.attachment_count} docs</span>
                )}
              </span>
            </span>
          </button>
        );
      })}
      {open.length > 12 && (
        <p className="acb-clock-more">{open.length - 12} more open — see the full list.</p>
      )}
    </div>
  );
}

/**
 * Coverage gaps (W6) — where the security stack is open at this account.
 *
 * Reads the SAME query the security heatmap does, so having both on a board
 * costs one fetch. The two answer different questions on purpose: the heatmap
 * is "who sells what here" and needs eighteen vendor rows to say it; this is
 * "what is open", which is a count, and which an empty column in a matrix
 * cannot say because empty reads as background.
 *
 * The endpoint returns all fourteen columns whether or not they have spend —
 * `PRODUCT_CATEGORIES` is mapped straight to `columns` server-side — which is
 * what makes a gap representable at all.
 *
 * REBUILT after Ryan's review. Three things were wrong and all three were his
 * complaints, in his words:
 *
 *   "I'm not sure what this is really telling us." Coverage was `spend > 0`.
 *   At DGS that made a $5 SOAR line and a $513 WAF line "covered" beside
 *   $1.28M of Endpoint, and the centre read "1 GAP" at an account with five
 *   categories nobody has meaningfully deployed. The chart now applies a
 *   materiality floor and says what it is on the card.
 *
 *   "There really are not a lot of relevant labels on the pieces of PIE."
 *   There were none — the only text was the centre number. Every segment is
 *   labelled now, and the side panel is a list of all thirteen rather than a
 *   legend of three counts.
 *
 *   "I can't drill down in any meaningful way to see the data behind it."
 *   Gaps were the ONE state that did not open anything, which is backwards for
 *   a widget named after them. Every category opens now: a covered one shows
 *   its vendors and each opens that vendor's lines; a gap shows who the rest of
 *   California buys the category from, which is the question that follows
 *   "nobody here buys it" and is frequently the better lead. Cloud Security at
 *   DGS is a gap, and statewide it is led by Palo Alto Networks.
 */
/** Statewide vendors listed before the remainder is summarised. */
const NEWS_LIMIT = 5;
const MARKET_ROWS = 5;

function CoverageRingWidget({ size }) {
  const { selectedDepartment } = useBoardAccount();
  const departmentId = selectedDepartment?.department_id;
  const account = accountKey(selectedDepartment);
  const [picked, setPicked] = useState(null);
  const [drill, setDrill] = useState(null);

  const { dateRange } = useTimeFilterContext();
  const q = useQuery({
    // Same key AND same params as the panel and the ring — widgetTileForms
    // guards this, and rightly: three widgets reading one endpoint with three
    // windows would disagree on screen and pay for the endpoint three times.
    queryKey: ['accounts-security-coverage', departmentId, dateRange?.startDate, dateRange?.endDate],
    queryFn: () => apiClient.get(
      '/api/vendor-analysis/security-coverage/vendor-breakdown',
      {
        params: {
          department_id: departmentId,
          ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
          ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        },
      },
    ),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });

  // One row per category: total spend, and every vendor in it — not just the
  // leader. The leader alone is what made the old drill feel thin: clicking
  // "Endpoint" opened McAfee's lines and silently dropped the other four
  // vendors in the same category.
  const categories = useMemo(() => {
    const d = q.data;
    if (!d?.columns?.length) return [];
    const byCol = new Map();
    for (const cell of d.cells || []) {
      const entry = byCol.get(cell.colId) || { spend: 0, vendors: new Map() };
      entry.spend += cell.spend || 0;
      const rowName = (d.rows || []).find((r) => String(r.id) === String(cell.rowId))?.label;
      if (rowName) entry.vendors.set(rowName, (entry.vendors.get(rowName) || 0) + (cell.spend || 0));
      byCol.set(cell.colId, entry);
    }
    return d.columns
      // 'Unmapped' is a classification residue, not a category anyone sells
      // into — on a coverage wheel it would read as a fourteenth opening.
      .filter((col) => col.id !== 'Unmapped')
      .map((col) => {
        const entry = byCol.get(col.id);
        const vendors = entry
          ? [...entry.vendors.entries()]
            .map(([name, spend]) => ({ name, spend }))
            .sort((a, b) => b.spend - a.spend)
          : [];
        return {
          id: col.id,
          label: col.label || col.id,
          spend: entry?.spend || 0,
          vendors,
          topVendor: vendors[0]?.name || null,
          vendorCount: vendors.length,
          ours: !!vendors[0] && /palo alto/i.test(vendors[0].name),
        };
      });
  }, [q.data]);

  const floor = useMemo(() => materialityFloor(categories), [categories]);
  const pickedState = picked ? coverageState(picked, floor) : null;

  // Statewide market for the selected category. Only fetched when the selected
  // category has nothing of its own to show — a covered category already
  // carries its answer in the payload above, and asking anyway would spend a
  // 2-second statewide sweep to tell the reader something they can see.
  const marketFor = pickedState && isOpenCoverage(pickedState) ? picked : null;
  const market = useQuery({
    queryKey: ['accounts-category-market', marketFor?.id],
    queryFn: () => apiClient.get(
      '/api/vendor-analysis/security-coverage/category-market',
      { params: { category: marketFor.id } },
    ),
    enabled: !!marketFor,
    staleTime: 10 * 60 * 1000,
  });

  const openVendorLines = useCallback((category, vendorName, vendorSpend) => {
    if (!departmentId || !vendorName) return;
    setDrill({
      title: `${vendorName} · ${category.label}`,
      // The ring is windowed by the topbar filter, so the drill must be too.
      // It used to say "all time" and ask for all time, under a footer
      // promising the list reconciles to the chart figure — which is the
      // windowed one. The two could not agree, and the footer said they should.
      contextLabel: `${account}${dateRange?.startDate ? ` · ${dateRange.startDate} – ${dateRange.endDate || 'now'}` : ' · all time'}`,
      expected: vendorSpend,
      queryKey: ['accounts-drill-security', departmentId, vendorName, category.id,
        dateRange?.startDate, dateRange?.endDate],
      path: `/api/vendor-analysis/${encodeURIComponent(vendorName)}/transactions`,
      params: {
        department: departmentId,
        classifiedVendor: vendorName,
        securityCategory: category.id,
        ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        limit: 500,
      },
    });
  }, [departmentId, account, dateRange]);

  /*
   * A GAP's statewide list opens too.
   *
   * "Palo Alto Networks · $4.1M · 12 agencies" was inert text — the one row on
   * this card a reader most wants to open, because it is the answer to "nobody
   * here buys it, so who does". It now opens the lines behind it across every
   * agency, and the drill table already carries an Agency column with a
   * multiselect filter, so "what do the other twelve have" is one click and one
   * filter rather than a different screen.
   *
   * WINDOWED TO MATCH THE FIGURE IT CAME FROM. The market card sums the last N
   * years (3 by default); an unwindowed drill would list lines back to 2018
   * under a footer promising they reconcile to $4.1M. Same rule as the
   * account-scoped drill above.
   *
   * limit 200, not the 500 the account drill uses: 500 statewide rows timed the
   * endpoint out when measured on SOAR, and a drill that 500s is worse than one
   * that truncates.
   */
  const openStatewideLines = useCallback((category, vendor) => {
    if (!category || !vendor?.vendor) return;
    const years = market.data?.years || 3;
    const since = new Date();
    since.setFullYear(since.getFullYear() - years);
    const startDate = since.toISOString().slice(0, 10);
    setDrill({
      title: `${vendor.vendor} · ${category.label} · statewide`,
      contextLabel: `Every California agency · last ${years} years · none of it at ${account}`,
      expected: vendor.spend,
      queryKey: ['accounts-drill-category-market', category.id, vendor.vendor, startDate],
      path: `/api/vendor-analysis/${encodeURIComponent(vendor.vendor)}/transactions`,
      params: {
        classifiedVendor: vendor.vendor,
        securityCategory: category.id,
        startDate,
        // MATCH THE POPULATION THE FIGURE COUNTED. The market snapshot applies
        // no confidence floor; /transactions applies one by default. Measured
        // on SOAR × Palo Alto: the card says $4.1M across 12 agencies, and the
        // drill answered $3.05M across 7 until this was set — under a footer
        // promising the list reconciles to the card. Same mismatch the PANW
        // footprint drill had, for the same reason.
        includeLowConfidence: true,
        limit: 200,
      },
    });
  }, [account, market.data]);

  if (!departmentId) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    return (
      <WidgetStatus
        state="error"
        message={`Security coverage unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }
  if (categories.length === 0) {
    return <WidgetStatus state="empty" message="No security coverage recorded for this agency." />;
  }

  const counts = categories.reduce((acc, c) => {
    acc[coverageState(c, floor)] += 1;
    return acc;
  }, { ours: 0, held: 0, thin: 0, gap: 0 });

  if (size === 'tile') {
    // The same figure the wheel puts in its middle, which is the point of
    // having chosen that figure: the tile is the widget with everything except
    // its headline taken away, not a different widget.
    const openCount = counts.gap + counts.thin;
    return (
      <TileFigure
        value={openCount}
        label="Open to us"
        sub={`of ${categories.length}`}
        tone={openCount > 0 ? 'warn' : 'good'}
      />
    );
  }

  return (
    <div className="acb-ring">
      <div className="acb-ring-chart">
        <CoverageRing
          categories={categories}
          selectedId={picked?.id}
          onPick={setPicked}
          fill
        />
      </div>

      <div className="acb-ring-side">
        {picked ? (
          <div className="acb-ring-detail">
            <div className="acb-ring-detail-head">
              <span className="acb-ring-cat">{picked.label}</span>
              <span className={`acb-ring-state acb-ring-state--${pickedState}`}>
                {COVERAGE_STATE_LABEL[pickedState]}
              </span>
            </div>

            {picked.spend > 0 && (
              <span className="acb-ring-spend">{fmtMoney(picked.spend)} recorded</span>
            )}

            {/* A covered category answers with its own vendors; each one opens
                the lines behind it, so the number on the card and the list it
                opens are the same number. */}
            {picked.vendors.length > 0 && (
              <ul className="acb-ring-vendors">
                {picked.vendors.slice(0, 6).map((v) => (
                  <li key={v.name}>
                    <button
                      type="button"
                      className="acb-ring-vendor"
                      onClick={() => openVendorLines(picked, v.name, v.spend)}
                    >
                      <span className="acb-ring-vendor-name">{v.name}</span>
                      <span className="acb-ring-vendor-spend">{fmtMoney(v.spend)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* An open category answers with the rest of California. */}
            {marketFor && (
              <div className="acb-ring-market">
                <span className="acb-ring-market-head">
                  {pickedState === 'gap'
                    ? `Nothing recorded at ${account}. Statewide:`
                    : `Token spend only at ${account}. Statewide:`}
                </span>
                {market.isLoading && <span className="acb-ring-hint">Looking across California…</span>}
                {market.error && (
                  <span className="acb-ring-hint">Statewide view unavailable.</span>
                )}
                {market.data && (market.data.vendors || []).length === 0 && (
                  <span className="acb-ring-hint">
                    No California agency has recorded spend here either.
                  </span>
                )}
                {market.data && (market.data.vendors || []).length > 0 && (
                  <>
                    <ul className="acb-ring-vendors">
                      {/* Capped so the list cannot push the ring out of its own
                          widget. The ring has a floor now and the side yields to
                          it, so an uncapped list would simply be clipped —
                          silently. Five plus a stated remainder is the same deal
                          the covered-category list already offers at six. */}
                      {market.data.vendors.slice(0, MARKET_ROWS).map((v) => (
                        <li key={v.vendor}>
                          <button
                            type="button"
                            className={`acb-ring-vendor${v.ours ? ' acb-ring-vendor--ours' : ''}`}
                            onClick={() => openStatewideLines(picked, v)}
                            title={`Open the ${v.vendor} ${picked.label} lines across all ${v.agencies} ${v.agencies === 1 ? 'agency' : 'agencies'}`}
                          >
                            <span className="acb-ring-vendor-name">{v.vendor}</span>
                            <span className="acb-ring-vendor-spend">
                              {fmtMoney(v.spend)} · {v.agencies} {v.agencies === 1 ? 'agency' : 'agencies'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {market.data.vendors.length > MARKET_ROWS && (
                      <span className="acb-ring-hint">
                        {`${market.data.vendors.length - MARKET_ROWS} more `}
                        {market.data.vendors.length - MARKET_ROWS === 1 ? 'vendor' : 'vendors'}
                        {' — open the full view'}
                      </span>
                    )}
                    <span className="acb-ring-foot">
                      {fmtMoney(market.data.statewideSpend)} statewide over {market.data.years} years
                      {market.data.ourShare > 0
                        ? ` · ours ${Math.round(market.data.ourShare * 100)}%`
                        : ' · none of it ours'}
                    </span>
                  </>
                )}
              </div>
            )}

            <button type="button" className="acb-ring-clear" onClick={() => setPicked(null)}>
              Back to summary
            </button>
          </div>
        ) : (
          <>
            <ul className="acb-ring-legend">
              <li><i className="acb-key acb-key--ours" />{COVERAGE_STATE_LABEL.ours} <b>{counts.ours}</b></li>
              <li><i className="acb-key acb-key--held" />{COVERAGE_STATE_LABEL.held} <b>{counts.held}</b></li>
              <li><i className="acb-key acb-key--thin" />{COVERAGE_STATE_LABEL.thin} <b>{counts.thin}</b></li>
              <li><i className="acb-key acb-key--gap" />{COVERAGE_STATE_LABEL.gap} <b>{counts.gap}</b></li>
            </ul>

            {/* The openings, by name and clickable. The old version listed the
                gap names as plain text — the one place the categories WERE
                written down was the one place you could not click them. */}
            {counts.gap + counts.thin > 0 && (
              <div className="acb-ring-open">
                <span className="acb-ring-open-head">Open to us</span>
                <div className="acb-ring-chips">
                  {categories
                    .filter((c) => isOpenCoverage(coverageState(c, floor)))
                    .sort((a, b) => a.spend - b.spend)
                    .map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        className={`acb-ring-chip acb-ring-chip--${coverageState(c, floor)}`}
                        onClick={() => setPicked(c)}
                      >
                        {c.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* The threshold is stated, because a number that changes what the
                chart claims and is written down nowhere is how "1 GAP" came to
                be believed at an account with five open categories. */}
            <p className="acb-ring-foot">
              Token spend is under {fmtMoney(floor)} — 1% of this account&rsquo;s
              recorded security spend.
            </p>
          </>
        )}
      </div>

      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        queryKey={drill?.queryKey || ['accounts-ring-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null
          ? `Chart figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.`
          : null}
      />
    </div>
  );
}

/**
 * Buying rhythm (W6) — which months this agency actually signs in.
 *
 * Calendar months come off the wire; the FISCAL mapping happens in the chart,
 * against whichever calendar the viewer has selected (invariant 8 — the axis
 * is the viewer's fiscal year, never the vendor's, and the boundary is read
 * off the registry rather than hard-coded to July).
 *
 * The basis is in the query key for the CHART, not the fetch: the months
 * themselves are calendar months and do not change when the toggle flips, so
 * re-fetching on a flip would be a wasted round trip. Only the bucketing moves.
 */
const RHYTHM_YEARS = 5;

/** Calendar months, short. The tile names one; the calendar draws all twelve. */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function BuyingRhythmWidget({ fill, size }) {
  const { selectedDepartment } = useBoardAccount();
  const { basis } = useFiscalBasisContext();
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;
  const [drill, setDrill] = useState(null);

  const q = useQuery({
    // Ask for exactly what the calendar draws. The service bounds the window
    // anyway, but a caller that requests eleven years to render five is how
    // the unbounded scan got shipped in the first place.
    queryKey: ['accounts-spend-by-month', account, RHYTHM_YEARS],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/spend-by-month`,
      { params: { sinceYear: new Date().getFullYear() - (RHYTHM_YEARS + 1) } },
    ),
    enabled: !!account,
    staleTime: 15 * 60 * 1000,
  });

  const cal = getFiscalCalendar(basis) || getFiscalCalendar(DEFAULT_FISCAL_CALENDAR_KEY);

  // A cell opens that calendar month's transactions. The window is the month
  // itself, so the list reconciles to the cell rather than to its fiscal year.
  const onPick = useCallback((cell) => {
    if (!departmentId) return;
    const y = cell.calendarMonth >= (cal?.fyStartMonth || 7)
      ? cell.fiscalYear - 1
      : cell.fiscalYear;
    const start = `${y}-${String(cell.calendarMonth).padStart(2, '0')}-01`;
    const endDate = new Date(y, cell.calendarMonth, 0);
    const end = `${y}-${String(cell.calendarMonth).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    setDrill({
      title: `${cell.label} ${y}`,
      contextLabel: `${account} · ${start} – ${end}`,
      expected: cell.spend,
      queryKey: ['accounts-drill-month', departmentId, start],
      path: '/api/v1/procurement/transactions',
      params: { dept: departmentId, startDate: start, endDate: end, limit: 500 },
    });
  }, [departmentId, account, cal]);

  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    const timedOut = q.error.code === 'AGGREGATE_TIMEOUT' || q.error.status === 504;
    return (
      <WidgetStatus
        state="error"
        message={timedOut
          ? 'The monthly totals took too long for this agency.'
          : `Buying rhythm unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }
  const months = q.data?.months || [];
  if (months.length === 0) {
    return <WidgetStatus state="empty" message="No dated spend recorded for this agency." />;
  }
  if (size === 'tile') {
    // Which month they sign in. The calendar's whole finding is that agencies
    // have one, and naming it is the entire takeaway.
    // `month` is 'YYYY-MM', not a number — Number('2026-03') is NaN, which
    // would have made every tile read "—" while the calendar beside it drew
    // twelve correct columns.
    const byMonth = new Map();
    for (const m of months) {
      const key = Number(String(m.month || '').slice(5, 7));
      if (!key) continue;
      byMonth.set(key, (byMonth.get(key) || 0) + (Number(m.spend) || 0));
    }
    const peak = [...byMonth.entries()].sort((a, b) => b[1] - a[1])[0];
    return peak
      ? (
        <TileFigure
          value={MONTH_ABBR[peak[0] - 1] || '—'}
          label="Peak month"
          sub={fmtMoney(peak[1])}
          tone="accent"
        />
      )
      : <TileFigure value={months.length} label="Months with spend" />;
  }

  return (
    <div className="acb-chart acb-rhythm">
      <BuyingRhythm
        months={months}
        fiscalStartMonth={cal?.fyStartMonth || 7}
        years={RHYTHM_YEARS}
        onPick={onPick}
        fill={fill}
      />
      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        queryKey={drill?.queryKey || ['accounts-rhythm-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null
          ? `Chart figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.`
          : null}
      />
    </div>
  );
}

/**
 * Gaining and losing (W7) — vendor rank year over year at this account.
 *
 * Replaces mounting the whole Competitive TAB in a cell. A trajectory answers
 * "how much"; this answers "compared to whom", which is the question a rep is
 * carrying — and the tab behind the header is still there for the full view.
 *
 * Ours is pinned into the chart even when it sits outside the ranked top:
 * measured on production, Palo Alto is absent from the top 6 at four of five
 * agencies, and "not in the top six, and here is who is" is the answer, not a
 * missing line.
 */
function DisplacementBumpWidget({ fill, size }) {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;
  const { years } = useTimeFilterContext();
  const [drill, setDrill] = useState(null);

  /*
   * THE WINDOW IS THE PAGE'S — frontend invariant 7, and the same shape as
   * `ShareOfWalletWidget`, which sits two cells away and calls this very
   * endpoint correctly.
   *
   * This asked for a hard-coded `years: 5` and kept no time dimension in its
   * key, so the board's filter moved from 3y to 1m with the chart unchanged —
   * and the empty state underneath it reads "No named vendors in this
   * window", naming a window the query never had. Both halves of that were
   * wrong in the same direction: it told the reader it was answering their
   * question and answered a fixed five-year one.
   *
   * `years` is null on "all time" and the param is omitted then, matching the
   * sibling widget exactly.
   */
  const q = useQuery({
    queryKey: ['accounts-vendor-trajectory', account, years ?? null, 6],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/vendor-trajectory`,
      { params: { ...(years ? { years } : {}), topN: 6 } },
    ),
    enabled: !!account,
    staleTime: 15 * 60 * 1000,
  });

  // A mark opens that vendor's lines for that fiscal year. The window is the
  // STATE fiscal year because the matview behind the chart is baked to it —
  // drilling on the viewer's basis would open a window the mark never showed.
  const onPick = useCallback((mark) => {
    if (!departmentId || !mark?.vendor) return;
    const start = `${mark.year - 1}-07-01`;
    const end = `${mark.year}-06-30`;
    setDrill({
      title: `${mark.vendor} · FY${String(mark.year).slice(-2)}`,
      contextLabel: `${account} · ${start} – ${end}`,
      expected: mark.spend,
      queryKey: ['accounts-drill-bump', departmentId, mark.vendor, mark.year],
      path: `/api/vendor-analysis/${encodeURIComponent(mark.vendor)}/transactions`,
      params: { department: departmentId, startDate: start, endDate: end, limit: 500 },
    });
  }, [departmentId, account]);

  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    return (
      <WidgetStatus
        state="error"
        message={`Competitive position unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }
  const points = q.data?.points || [];
  if (points.length === 0) {
    // "No classified technology spend" and "all of it sits under a vendor we
    // could not resolve" are different facts, and at an agency whose whole
    // matview row set is the N/A bucket the first one is false.
    const orphaned = Number(q.data?.unattributed) || 0;
    return (
      <WidgetStatus
        state="empty"
        message={orphaned > 0
          ? `No named vendors in this window — ${fmtMoney(orphaned)} of classified spend has no resolved vendor.`
          : 'No classified technology spend recorded for this agency in this window.'}
      />
    );
  }

  if (size === 'tile') {
    // Our rank among named vendors in the most recent year on the chart. The
    // bump chart's whole subject is position, so the tile is the position.
    const latest = Math.max(...points.map((p) => p.year));
    const thisYear = points
      .filter((p) => p.year === latest)
      .sort((a, b) => b.spend - a.spend);
    const idx = thisYear.findIndex((p) => /palo alto/i.test(p.vendor));
    return idx >= 0
      ? (
        <TileFigure
          value={`#${idx + 1}`}
          label="Our rank"
          sub={`of ${thisYear.length} · FY${String(latest).slice(-2)}`}
          tone={idx === 0 ? 'good' : idx < 3 ? 'accent' : 'warn'}
        />
      )
      : (
        <TileFigure
          value={thisYear.length}
          label="Vendors ranked"
          sub={`none of them ours · FY${String(latest).slice(-2)}`}
          tone="warn"
        />
      );
  }

  // The unattributed bucket is not on the chart and is frequently bigger than
  // everything that is — $1.41B against $61M of named vendors at DGS. Dropping
  // it is right (it is not a competitor); dropping it silently is not.
  const unattributed = Number(q.data?.unattributed) || 0;
  // Name the years drawn. The endpoint floors the span at two and caps it at
  // twelve, so the window the reader picked and the window the chart got are
  // not always the same number — and a bump chart that quietly widened a
  // one-month filter to two fiscal years should say so rather than let the
  // topbar speak for it.
  const span = fiscalSpanLabel(points.map((p) => p.year));
  const scopeNote = [
    q.data?.scope || 'classified technology spend',
    span,
    unattributed > 0 ? `${fmtMoney(unattributed)} unattributed, not shown` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="acb-chart">
      <DisplacementBump
        points={points}
        ourPinned={q.data?.ourPinned}
        scopeNote={scopeNote}
        fill={fill}
        onPick={onPick}
      />
      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        queryKey={drill?.queryKey || ['accounts-bump-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null
          ? `Chart figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.`
          : null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/* ── Purchase history ──────────────────────────────────────────────────────
 *
 * "A running purchase history, especially security related" — Ryan's first
 * goal for the board, and nothing served it. `renewals` is paper expiring,
 * not paper signed; `procurement-summary` was a prose blob that rendered every
 * vendor at $0; the ceiling gauge ranks by SIZE for a different question.
 *
 * This takes procurement-summary's SLOT rather than a new catalog entry. The
 * TekStak catalog sits one type below the headroom guard
 * (`boardRegistry.test.js`: claimed <= MAX_DASHBOARD_WIDGETS - 2), so a new
 * type is a scarce thing to spend and this is the same subject answered
 * better — the same move `competitive` made when it became a bump chart. The
 * type string stays `procurement-summary` so no saved board needs migrating.
 *
 * Against the board's three rules:
 *   drills   every row opens the real purchase order
 *   visual   one stable colour per security bucket (shared with the heatmap,
 *            see securityCategories.ts), and a bar whose filled portion is the
 *            CYBER share of that PO rather than its size alone
 *   live     All/Cyber toggle plus per-bucket filter chips
 */
function PurchaseHistoryWidget({ size }) {
  const { dateRange } = useTimeFilterContext();
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;
  const [cyberOnly, setCyberOnly] = useState(false);
  const [bucket, setBucket] = useState(null);
  // "product not classified" is the most common thing this list says on some
  // agencies, and it was scattered — you could see individual rows but not how
  // many, and nothing collected them. The PO modal has offered correct / flag /
  // research per line all along (ClassificationChip); what was missing was a
  // way to FIND the lines that need it.
  const [unclassifiedOnly, setUnclassifiedOnly] = useState(false);
  const [openPo, setOpenPo] = useState(null);


  /*
   * THE GLOBAL TIME FILTER APPLIES HERE TOO (frontend invariant 7).
   *
   * This widget used to send nothing but `limit`, so it answered "the newest
   * 40 purchases, ever" whatever the topbar said. Measured 2026-09-17 on State
   * Controller: moving the filter from 3y to 1y re-captioned Security coverage
   * to "last 1y" and left this list showing purchases back to 2024 — outside
   * the window, beside widgets that honour it, with nothing on its face to say
   * so.
   *
   * The endpoint takes `sinceYear`, not a date range, so the window is floored
   * to the calendar year the filter starts in. That is coarser than the filter
   * by up to eleven months and it is the honest direction to be coarse in:
   * showing a little MORE than asked and saying which year it starts from
   * beats silently dropping purchases the rep expected to see.
   *
   * `sinceYear` is in the query key or the list would keep its first answer
   * for the whole session — the defect this replaces, one layer down.
   *
   * `security=only` is asked of the SERVER rather than filtered here, because
   * the server picks the newest rows before it cuts: filtering client-side
   * would answer "the cyber POs among the newest 50", which on CDT is two.
   * The bucket chips filter what came back, which is the cheap half.
   */
  const sinceYear = dateRange?.startDate
    ? Number(String(dateRange.startDate).slice(0, 4))
    : null;
  const q = useQuery({
    queryKey: ['accounts-purchase-history', account, cyberOnly, sinceYear],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/purchase-history`,
      {
        params: {
          limit: 40,
          ...(Number.isFinite(sinceYear) && sinceYear ? { sinceYear } : {}),
          ...(cyberOnly ? { security: 'only' } : {}),
        },
      },
    ),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const hasNoProduct = (p) => !(p.products && p.products.length);

  const rows = useMemo(() => {
    const all = q.data?.purchaseOrders || [];
    const byBucket = bucket ? all.filter((p) => (p.categories || []).includes(bucket)) : all;
    return unclassifiedOnly ? byBucket.filter(hasNoProduct) : byBucket;
  }, [q.data, bucket, unclassifiedOnly]);

  /*
   * THE LIST FITS THE CELL; IT DOES NOT SCROLL INSIDE IT.
   *
   * Ryan's rule, and he has said it twice: "scrolling in the widget is never
   * the answer. resizing rearranging reconfiguring that is the route we need
   * to go." This was the last widget still doing it — 40 rows, 2,208px of
   * content in a 416px box. Worse, it swallowed the PAGE scroll: a wheel over
   * the widget moved the list, so scrolling the board left a sticky month
   * header stranded over blank space and read as a broken widget.
   *
   * So it renders what fits and STATES the remainder, the same shape
   * SecurityCoverageHeatmap uses for its dropped rows. Fewer rows cannot
   * change the container's height — it is a flex child of a fixed board cell —
   * so measuring here has no feedback path back into the measurement, which is
   * what made CeilingGauge loop. The 2px floor is the same guard for the same
   * reason.
   */
  const rowsRef = useRef(null);
  const [fit, setFit] = useState(null);
  useEffect(() => {
    const el = rowsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let lastH = -1;
    const measure = () => {
      const h = el.clientHeight;
      if (h <= 0 || Math.abs(h - lastH) < 2) return;
      lastH = h;
      // A month heading is not a row — measured 24px against 54px — so the
      // budget is kept in PIXELS rather than in row counts. The old version
      // reserved exactly one row of slack for headings, which over-reserves
      // when the visible rows fall in one month and under-reserves the moment
      // they do not.
      const row = el.querySelector('.acb-hist-row');
      const head = el.querySelector('.acb-hist-month');
      setFit({
        h,
        rowH: row ? Math.max(28, Math.round(row.getBoundingClientRect().height)) : 44,
        monthH: head ? Math.max(12, Math.round(head.getBoundingClientRect().height)) : 24,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    /*
     * `rows.length`, not `[]`. On mount the query is still loading, so the
     * component returns a WidgetStatus, the ref is null and this bails — and
     * with an empty dependency list it never ran again, so the observer was
     * never attached and the list rendered ALL of its rows into a cell sized
     * for six. It looked fine in the browser only because react-query rehydrates
     * a persisted cache, which makes the data present on the very first render;
     * the path with a cold cache had no measurement at all.
     *
     * Safe against the feedback loop this file warns about twice: `rows` is the
     * filtered response, and nothing the measurement decides can change its
     * length — only WHICH of them get drawn.
     */
  }, [rows.length]);

  /*
   * A TRUNCATED LIST MUST NOT BE A BLIND PREFIX.
   *
   * This cut `rows.slice(0, fitCount)` — the newest N — and the newest N is
   * not a sample of an account, it is whatever happened last. Measured on
   * Secretary of State 2026-09-18: the six drawn rows came to $15,448 and the
   * footnote underneath admitted the 34 it had dropped were worth $4.9M. The
   * rep saw $168 of office supplies; a $1.9M Veritas purchase from two months
   * earlier was row 30. The widget was rendering 0.3% of the money and giving
   * no sign that the 99.7% was ordered differently from what it showed.
   *
   * So the CUT is by size and the ORDER stays by date. The list is still a
   * chronology — the dates and month headings are untouched — but the rows
   * that survive it are the ones that carry the account. Every hidden row is
   * then genuinely smaller than every drawn one, which is what lets the
   * footnote say "smaller" and be true.
   *
   * Budgeted in pixels because a kept row may or may not drag a month heading
   * in with it. `fit` depends only on the CELL's geometry, never on how many
   * rows were kept, so there is no path from this back into the measurement —
   * the loop CeilingGauge hit.
   */
  const shownRows = useMemo(() => {
    // Null until the first measurement, and rendering everything for that one
    // frame is correct — it is what gives the observer a row to measure.
    if (!fit) return rows;
    const { h, rowH, monthH } = fit;
    const kept = new Set();
    const months = new Set();
    let used = 0;
    for (const p of [...rows].sort((a, b) => (b.total || 0) - (a.total || 0))) {
      const m = (p.startDate || '').slice(0, 7);
      const cost = rowH + (months.has(m) ? 0 : monthH);
      // Three rows minimum even in a cell too short for them. An empty list
      // under a populated toolbar reads as "this agency buys nothing", which
      // is the one thing it never means.
      if (used + cost > h && kept.size >= 3) break;
      used += cost;
      kept.add(p.documentNumber);
      months.add(m);
    }
    return rows.filter((p) => kept.has(p.documentNumber));
  }, [rows, fit]);

  const shownIds = useMemo(
    () => new Set(shownRows.map((p) => p.documentNumber)),
    [shownRows],
  );
  const hiddenRows = rows.length - shownRows.length;
  const hiddenSpend = rows.reduce(
    (sum, p) => (shownIds.has(p.documentNumber) ? sum : sum + (Number(p.total) || 0)),
    0,
  );

  // Counted over everything that came back, not over the filtered rows, so the
  // number does not shrink as you narrow toward it.
  const unclassifiedCount = useMemo(
    () => (q.data?.purchaseOrders || []).filter(hasNoProduct).length,
    [q.data],
  );

  // Buckets actually present, so the chips describe THIS account rather than
  // offering eleven filters that all return nothing.
  const buckets = useMemo(() => {
    const seen = new Map();
    for (const p of (q.data?.purchaseOrders || [])) {
      for (const c of (p.categories || [])) seen.set(c, (seen.get(c) || 0) + 1);
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1]);
  }, [q.data]);

  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    // The typed timeout says something different from a real failure, and the
    // difference matters: an empty feed reads as "they buy nothing".
    const timedOut = q.error.code === 'AGGREGATE_TIMEOUT';
    return (
      <WidgetStatus
        state="error"
        message={timedOut
          ? 'The purchase history took too long for this agency — it is one of the largest.'
          : `Purchase history unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }

  const all = q.data?.purchaseOrders || [];
  /*
   * The bar scale is set by the widest row ON SCREEN, not the widest in the
   * response. Scaled to the whole set, a cell holding six four-figure POs
   * beside a hidden $1.9M one drew six bars at the 2% floor — identical stubs
   * encoding nothing. A scale whose maximum is not drawn is not a scale.
   */
  const widest = Math.max(...shownRows.map((p) => p.total || 0), 1);

  /*
   * An empty result does NOT take the toolbar with it. Returning a bare
   * WidgetStatus here was the first version, and it was a dead end: filter to
   * Cyber on an agency with none, and the control you would use to get back to
   * All disappears along with the rows. A filter that can strand the reader is
   * worse than no filter. The empty state lives in the rows area instead.
   */
  let lastMonth = null;

  if (size === 'tile') {
    // When they last bought, not how much. A history's headline is recency —
    // "they signed something 11 days ago" is the fact that changes a call.
    const newest = all.find((p) => p.startDate);
    if (!newest) return <TileFigure value={all.length} label="Purchases" />;
    const ago = -daysUntil(newest.startDate);
    const cyber = all.reduce((sum, p) => sum + (Number(p.securityTotal) || 0), 0);
    return (
      <TileFigure
        value={ago <= 0 ? 'Today' : `${ago}d`}
        label="Since last buy"
        sub={cyber > 0 ? `${fmtMoney(cyber)} cyber` : `${all.length} POs`}
        tone={ago <= 30 ? 'accent' : 'neutral'}
      />
    );
  }

  return (
    <div className="acb-hist">
      <div className="acb-hist-bar">
        <div className="acb-seg" role="group" aria-label="Filter purchases">
          <button
            type="button"
            className={`acb-seg-btn${cyberOnly ? '' : ' is-on'}`}
            onClick={() => { setCyberOnly(false); setBucket(null); }}
          >
            All
          </button>
          <button
            type="button"
            className={`acb-seg-btn${cyberOnly ? ' is-on' : ''}`}
            onClick={() => { setCyberOnly(true); setBucket(null); }}
          >
            Cyber
          </button>
        </div>
        {unclassifiedCount > 0 && (
          <button
            type="button"
            className={`acb-hist-unmapped${unclassifiedOnly ? ' is-on' : ''}`}
            onClick={() => setUnclassifiedOnly((v) => !v)}
            aria-pressed={unclassifiedOnly}
            title={`${unclassifiedCount} of these purchase orders have no classified product. Open one to correct, flag or research its lines.`}
          >
            {unclassifiedCount} unclassified
          </button>
        )}
        {/* COUNT WHAT THE FILTERS LEFT, not what the fetch returned.
            "40 of 40" sat directly above "37 more purchases" — both true (40
            came back, 3 were drawn) and flatly contradictory to read. Unfiltered
            there is no "of" to state, so it names the WINDOW's total and leaves
            how many fit to the footer, which already says it. "40 purchases"
            was the first attempt and is no better: beside "37 more purchases"
            it still reads as forty on screen. */}
        <span
          className="acb-hist-count"
          title={`${all.length} purchase orders returned for this window; ${rows.length} match the filters above.`}
        >
          {bucket || unclassifiedOnly || cyberOnly
            ? `${rows.length} of ${all.length}`
            : `${all.length} in this window`}
          {q.data?.sinceYear ? ` · since ${q.data.sinceYear}` : ''}
        </span>
      </div>

      {buckets.length > 0 && (
        <div className="acb-hist-chips cq-hide-glance">
          {buckets.map(([c, n]) => (
            <button
              type="button"
              key={c}
              className={`acb-hist-chip${bucket === c ? ' is-on' : ''}`}
              style={{ '--c': securityCategoryColor(c) }}
              onClick={() => setBucket(bucket === c ? null : c)}
              title={`${n} purchase order${n === 1 ? '' : 's'} touching ${c}`}
            >
              <i aria-hidden="true" />{c}
            </button>
          ))}
        </div>
      )}

      {openPo && (
        <PoDetailModal
          documentNumber={openPo.documentNumber}
          departmentId={departmentId}
          startDate={openPo.startDate}
          isOpen
          onClose={() => setOpenPo(null)}
        />
      )}

      <div className="acb-hist-rows" ref={rowsRef}>
        {rows.length === 0 && (
          <p className="acb-hist-empty">
            {unclassifiedOnly
              ? 'Every purchase order here has at least one classified product.'
              : cyberOnly
                ? `No cybersecurity purchases recorded since ${q.data?.sinceYear || 'the window start'}.`
                : bucket
                  ? `Nothing in ${bucket} among these purchases.`
                  : 'No dated purchase orders recorded for this agency.'}
          </p>
        )}
        {shownRows.map((p) => {
          const month = (p.startDate || '').slice(0, 7);
          const newMonth = month && month !== lastMonth;
          if (newMonth) lastMonth = month;
          const share = p.total > 0 ? Math.min(1, p.securityTotal / p.total) : 0;
          const width = Math.max(2, ((p.total || 0) / widest) * 100);
          const lead = p.categories && p.categories[0];
          return (
            <React.Fragment key={p.documentNumber}>
              {newMonth && (
                <div className="acb-hist-month">
                  {new Date(`${month}-01T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </div>
              )}
              {/* A plain button, NOT PoLink.
                  PoLink styles its content as a PO NUMBER — monospace, in the
                  link colour — which is right when the content is a number and
                  wrong when it is a whole row. Wrapping the row in it made 120
                  leaf nodes monospace, a quarter of all the text on the board,
                  and painted every supplier name link-blue. PoLink's own
                  docstring says to use PoDetailModal directly when you want the
                  modal without the link styling; this does that. */}
              <button
                type="button"
                className="acb-hist-row"
                onClick={() => setOpenPo({ documentNumber: p.documentNumber, startDate: p.startDate })}
                title={[
                  `${p.supplier || 'Unknown supplier'} — ${fmtMoney(p.total)} across ${p.lineCount} line${p.lineCount === 1 ? '' : 's'}`,
                  (p.products && p.products.length)
                    ? p.products.join(', ')
                    : 'No classified product — open to correct, flag or research these lines',
                ].join('\n')}
              >
                <span className="acb-hist-day">{(p.startDate || '').slice(8, 10)}</span>
                <span className="acb-hist-main">
                  <span className="acb-hist-supplier">{p.supplier || 'Unknown supplier'}</span>
                  {/* WHAT was bought, under WHO sold it (Ryan, 2026-09-14):
                      the reseller name alone does not say whether this was a
                      firewall or a printer. An unclassified PO says so rather
                      than leaving the line blank — 9 of CDT's 40 most recent
                      have no classified line, and a blank there would read as
                      "nothing to see" instead of "we could not tell". */}
                  <span className="acb-hist-products">
                    {(p.products && p.products.length)
                      ? (
                        <>
                          {p.products.slice(0, 2).join(' · ')}
                          {p.productCount > 2 ? ` · +${p.productCount - 2} more` : ''}
                        </>
                      )
                      : <em className="acb-hist-unclassified">product not classified</em>}
                  </span>
                  <span className="acb-hist-track" aria-hidden="true">
                    {/* Width is the PO's size; the filled head is the cyber
                        share of it. One bar answers "how big" and "how much of
                        it is mine" without a second column. */}
                    <span
                      className="acb-hist-fill"
                      style={{ width: `${width}%`, '--c': securityCategoryColor(lead) }}
                    >
                      <span className="acb-hist-cyber" style={{ width: `${share * 100}%` }} />
                    </span>
                  </span>
                </span>
                <span className="acb-hist-money">
                  <span className="acb-hist-total">{fmtMoney(p.total)}</span>
                  {p.isSecurity && p.securityTotal < p.total && (
                    <span className="acb-hist-sec">{fmtMoney(p.securityTotal)} cyber</span>
                  )}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* What is not drawn is STATED — the same rule the coverage heatmap
          follows. A list quietly showing eight of forty invites the reader to
          treat what they can see as the whole account. */}
      {hiddenRows > 0 && (
        <p className="ts-foot acb-hist-trunc">
          {`${hiddenRows} smaller ${hiddenRows === 1 ? 'purchase' : 'purchases'}`}
          {hiddenSpend > 0 ? ` · ${fmtMoney(hiddenSpend)} between them` : ''}
          {' — make this widget taller, or open the full view'}
        </p>
      )}
    </div>
  );
}


/* ── Palo Alto footprint ───────────────────────────────────────────────────
 *
 * Replaces `pan-trajectory` on TekStak, and the reason is a number rather than
 * a preference: the trajectory asked the SUPPLIER path, Palo Alto has no
 * supplier rows in California procurement at all, and the card therefore read
 * "No Palo Alto spend recorded at this agency" at CDT — which holds $13.2M of
 * Palo Alto product, bought through NWN Solutions, Enterprise Networking
 * Solutions and OPTM West.
 *
 * The heatmap reads the PRODUCT path and names the channel underneath, because
 * a Palo Alto chart with no reseller anywhere on it invites the same wrong
 * reading the trajectory gave. The footer states the claim being made —
 * product bought, not revenue — since the product path is the wider one.
 */
function PanwFootprintWidget({ fill, size }) {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  const departmentId = selectedDepartment?.department_id;
  const { basis } = useFiscalBasisContext();
  const { dateRange } = useTimeFilterContext();
  const [drill, setDrill] = useState(null);

  // Must match AccountsDashTab's derivation EXACTLY: both query under the same
  // react-query key, so a different window here splits the cache and the Key
  // numbers cards and this heatmap start disagreeing about which years exist.
  const panwWindow = footprintWindow(dateRange);

  const q = useQuery({
    // basis is in the key: flipping the toggle must refetch, not re-label last
    // calendar's buckets (invariant 8).
    queryKey: ['accounts-panw-footprint', account, basis,
      panwWindow.startDate, panwWindow.endDate],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/panw-footprint`,
      {
        params: {
          ...(basis && basis !== DEFAULT_FISCAL_CALENDAR_KEY ? { basis } : {}),
          // The footprint honoured NO time filter: it asked all-time while the
          // topbar said 3y, so the heatmap drew five fiscal years ending two
          // years before the Key numbers cards beside it. Both ends go, as
          // real dates — see footprintWindow for why not `sinceYear`.
          ...(panwWindow.startDate ? { startDate: panwWindow.startDate } : {}),
          ...(panwWindow.endDate ? { endDate: panwWindow.endDate } : {}),
        },
      },
    ),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  /*
   * A cell opens the purchase-order lines that MAKE IT UP.
   *
   * This drill shipped with a title, a cache key and no request. The shared
   * modal fetches only when handed a `queryFn`; without one its internal query
   * is a no-op that resolves `{ rows: [] }`. So every cell on this heatmap
   * opened a modal titled "Palo Alto · SSE/SASE · FY24" over "0 rows · $0" —
   * the title described a query nobody ran.
   *
   * The request has to ask for the SAME population the cell counted, and the
   * footprint's population is unusual: the PRODUCT path (`tc.vendor_name ILIKE
   * Palo Alto`), with no component-confidence floor and no supplier evidence —
   * because Palo Alto never appears as a supplier in California procurement
   * and the supplier-evidence path reads $0 at agencies holding millions. The
   * transactions endpoint applies both by default, so `includeLowConfidence`
   * switches them off; `securityCategory` reuses the same mapping join; and the
   * window is the fiscal year on the VIEWER's calendar, from the same registry
   * row the chart bucketed with (invariant 8).
   *
   * `Unmapped` is the one cell the endpoint cannot express — it is "no mapping
   * row", and `spm.product_category = 'Unmapped'` matches nothing — so that
   * cell opens the whole year's Palo Alto lines and says so, with the year's
   * total as the figure to reconcile to.
   */
  const openCell = useCallback((cell) => {
    if (!departmentId || !cell) return;
    // Hooks sit above the early returns, so the calendar and the year's cells
    // are derived here rather than borrowed from the render below.
    const cal = getFiscalCalendar(basis) || getFiscalCalendar(DEFAULT_FISCAL_CALENDAR_KEY);
    /*
     * THE DRILL OPENS THE WINDOW THE CELL COUNTED, not the whole fiscal year.
     *
     * The heatmap takes the topbar window, so its leading and trailing columns
     * are PART years. This opened `fiscalYearRange(cell.fy)` regardless, and on
     * CDT at 3y the FY24 cell read $67K while the list it opened totalled
     * $864K — thirteen times, under a footer promising the two reconcile. Only
     * the clipped columns disagreed; FY25 and FY26 sit wholly inside the
     * window and matched exactly, which is how it survived this long.
     */
    const { start, end, clipped } = clipRangeToWindow(fiscalYearRange(cell.fy, cal), panwWindow);
    const fyLabel = `FY${String(cell.fy).slice(-2)}`;
    const unmapped = cell.category === 'Unmapped';
    const yearTotal = (q.data?.cells || [])
      .filter((c) => c.fy === cell.fy)
      .reduce((sum, c) => sum + (Number(c.spend) || 0), 0);
    // Say it is a part year. Without this the reader sees a fiscal-year title
    // over a figure no full year would produce and has no way to know why.
    const partYear = clipped ? ` · ${fyLabel} clipped to the time filter` : '';
    setDrill({
      title: `Palo Alto · ${cell.category} · ${fyLabel}`,
      contextLabel: unmapped
        ? `${account} · ${start} – ${end}${partYear} · every category (Unmapped is what the mapping could not place)`
        : `${account} · ${start} – ${end}${partYear}`,
      expected: unmapped ? yearTotal : cell.spend,
      // The window belongs in the key. Without it, narrowing the filter and
      // reopening the same cell serves the rows the wider window cached.
      queryKey: ['accounts-panw-drill', departmentId, cell.category, cell.fy, basis, start, end],
      path: `/api/vendor-analysis/${encodeURIComponent(PANW)}/transactions`,
      params: {
        department: departmentId,
        startDate: start,
        endDate: end,
        ...(unmapped ? {} : { securityCategory: cell.category }),
        // The cell counted every product-path line. Leaving the floor and the
        // supplier test on would open a subset of what the cell showed.
        includeLowConfidence: 'true',
        limit: 500,
      },
    });
    // The window ends are deps, not the object: `panwWindow` is rebuilt every
    // render, and a callback that captured a stale one would clip the drill to
    // the PREVIOUS filter while the cell showed the current one.
  }, [departmentId, account, basis, q.data, panwWindow.startDate, panwWindow.endDate]);


  if (!account) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) {
    const timedOut = q.error.code === 'AGGREGATE_TIMEOUT';
    return (
      <WidgetStatus
        state="error"
        message={timedOut
          ? 'The Palo Alto footprint took too long for this agency — it is one of the largest.'
          : `Palo Alto footprint unavailable — ${q.error.message}`}
        onRetry={() => q.refetch()}
      />
    );
  }

  const d = q.data || {};
  const cal = getFiscalCalendar(basis) || getFiscalCalendar(DEFAULT_FISCAL_CALENDAR_KEY);

  if (size === 'tile') {
    // Total Palo Alto PRODUCT bought here. The heatmap's finding is that this
    // number exists at all — PANW never appears as a supplier in California
    // procurement, so the supplier path reads $0 at agencies holding millions.
    const total = (d.cells || []).reduce((sum, c) => sum + (Number(c.spend) || 0), 0);
    return total > 0
      ? (
        <TileFigure
          value={fmtMoney(total)}
          label="Palo Alto"
          sub={`${(d.categories || []).length} product areas`}
          tone="accent"
        />
      )
      : <TileFigure value="None" label="Palo Alto" sub="no product recorded" tone="warn" />;
  }

  return (
    <>
      <VendorCategoryHeatmap
        categories={d.categories || []}
        years={d.years || []}
        cells={d.cells || []}
        resellers={d.resellers || []}
        fill={fill}
        scopeNote={`Palo Alto PRODUCT bought by this agency · ${cal?.label || 'California state fiscal year'}`}
        onPick={departmentId ? openCell : undefined}
      />
      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        vendor={PANW}
        departmentId={departmentId}
        queryKey={drill?.queryKey || ['accounts-panw-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null
          ? `Chart figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.`
          : null}
      />
    </>
  );
}

/*
 * Partner involvement — who sells Palo Alto into this account, on which
 * contract vehicle, and what ELSE they sell here (Ryan, 2026-09-14: "how good
 * of a partner they are to us, do they sell competing solutions?").
 *
 * Two views under one control: "Selling Palo Alto" groups partners by ETC /
 * SLP / no-LPA with a bar per partner stacked by security category and the
 * contract ids they rode; "Also selling" lists the same partners with every
 * other vendor they sold the account, competing security first.
 *
 * Every row drills to the purchase-order lines behind it: a partner opens
 * their Palo Alto lines, a competing chip opens THAT vendor's lines from the
 * same supplier — the transactions endpoint takes `supplier`, and the product
 * path needs the same no-floor / no-supplier-evidence switch the footprint
 * uses, or the drill opens a subset of what the bar showed.
 */
function PartnerInvolvementWidget({ fill, size }) {
  const { selectedDepartment } = useBoardAccount();
  const departmentId = selectedDepartment?.department_id;
  const account = accountKey(selectedDepartment);
  const { dateRange } = useTimeFilterContext();
  const [view, setView] = useState('selling');
  const [drill, setDrill] = useState(null);
  const window = {
    ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
    ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
  };

  const q = useQuery({
    queryKey: ['accounts-partner-involvement', departmentId, dateRange?.startDate, dateRange?.endDate],
    queryFn: () => apiClient.get(
      `/api/vendor-analysis/${encodeURIComponent(PANW)}/partner-involvement`,
      { params: { department_ids: departmentId, ...window } },
    ),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });

  const onPick = useCallback((partner, other) => {
    if (!departmentId || !partner) return;
    const vendor = other ? other.vendor : PANW;
    setDrill({
      title: `${partner.partner} · ${other ? other.vendor : 'Palo Alto'}`,
      contextLabel: `${account}${dateRange?.startDate ? ` · ${dateRange.startDate} – ${dateRange.endDate || 'now'}` : ''}${other?.competing ? ' · competing security' : ''}`,
      expected: other ? other.spend : partner.spend,
      queryKey: ['accounts-partner-drill', departmentId, partner.partner, vendor, dateRange?.startDate, dateRange?.endDate],
      vendor,
      path: `/api/vendor-analysis/${encodeURIComponent(vendor)}/transactions`,
      params: { department: departmentId, supplier: partner.partner, ...window, includeLowConfidence: 'true', limit: 500 },
    });
  }, [departmentId, account, dateRange]);

  if (!departmentId) return <WidgetStatus state="empty" message="No account selected." />;
  if (q.isLoading) return <WidgetStatus state="loading" />;
  if (q.error) return <WidgetStatus state="error" message={`Partner involvement unavailable — ${q.error.message}`} onRetry={() => q.refetch()} />;

  const d = q.data || { partners: [], totals: { spend: 0, partners: 0, competingPartners: 0, byVehicle: {} } };

  if (size === 'tile') {
    const n = d.totals?.partners || 0;
    const competing = d.totals?.competingPartners || 0;
    if (n === 0) return <TileFigure value="None" label="Partners" sub="no Palo Alto resold" variant="word" tone="warn" />;
    return (
      <TileFigure
        value={n}
        label={n === 1 ? 'Partner' : 'Partners'}
        sub={competing > 0 ? `${competing} also sell against us` : 'none sell against us'}
        tone={competing > 0 ? 'warn' : 'good'}
      />
    );
  }

  return (
    <>
      <PartnerInvolvement data={d} view={view} onView={setView} onPick={onPick} fill={fill} formatMoney={fmtMoney} />
      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        vendor={drill?.vendor || PANW}
        departmentId={departmentId}
        queryKey={drill?.queryKey || ['accounts-partner-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null ? `Widget figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.` : null}
      />
    </>
  );
}

/* ---------------------------------------------------------------------------
 * Tiles for the widgets that MOUNT a panel.
 *
 * These eleven cards have no body of their own — they mount a Dash section, an
 * intel section or a whole tab, each of which owns its fetch. So a tile cannot
 * ask the widget for a number; it has to go to the same source.
 *
 * EVERY HOOK BELOW REUSES THE PANEL'S OWN QUERY KEY, byte for byte. That is the
 * whole design: with the panel on the board the tile is a cache hit and costs
 * nothing, and without it the tile pays for one small read instead of mounting
 * a chart it cannot draw. A near-miss key — a different param, a different
 * order — would silently double every fetch on the board, which is the failure
 * this comment exists to prevent. If you change a key here, change it in
 * `AccountsDashTab` / `AccountsOverviewIntel` in the same commit.
 *
 * The twelve widgets still on the stub are the ones whose numbers live inside a
 * mounted component rather than behind a query key this file can name.
 */

/** The Dash tab's account-stats row. */
function useCoverageStats() {
  const { selectedDepartment } = useBoardAccount();
  const departmentId = selectedDepartment?.department_id;
  return useQuery({
    queryKey: ['accounts-coverage', departmentId],
    queryFn: () => apiClient.get('/api/gap-analysis/coverage', {
      params: { departmentId, limit: 1 },
    }),
    enabled: !!departmentId,
    staleTime: 60_000,
  });
}

/** Agency-wide spend by fiscal year. */
function useEvidence() {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  return useQuery({
    queryKey: ['accounts-evidence', account],
    queryFn: () => apiClient.get(`/api/v1/accounts/${encodeURIComponent(account)}/evidence`),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });
}

/** The DGS tier card. */
function usePurchasingAuthority() {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  return useQuery({
    queryKey: ['accounts-purchasing-authority', account],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/purchasing-authority`,
    ),
    enabled: !!account,
    staleTime: 60 * 60 * 1000,
  });
}

/** The agency profile: facts, drivers, funding model. */
function useProfile() {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  return useQuery({
    queryKey: ['accounts-profile', account],
    queryFn: () => apiClient.get(`/api/v1/accounts/${encodeURIComponent(account)}/profile`),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });
}

/** Top vendors and categories. */
function useProcurementSummary() {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  return useQuery({
    queryKey: ['accounts-procurement-summary', account],
    queryFn: () => apiClient.get('/api/v1/procurement/summary', { params: { account } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });
}

/** The security vendor x category matrix the ring and the heatmap both read. */
function useSecurityCoverage() {
  const { selectedDepartment } = useBoardAccount();
  const departmentId = selectedDepartment?.department_id;
  const { dateRange } = useTimeFilterContext();
  return useQuery({
    queryKey: ['accounts-security-coverage', departmentId, dateRange?.startDate, dateRange?.endDate],
    queryFn: () => apiClient.get(
      '/api/vendor-analysis/security-coverage/vendor-breakdown',
      {
        params: {
          department_id: departmentId,
          ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
          ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        },
      },
    ),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Resellers carrying Palo Alto, split by category. */
function usePartnersByCategory() {
  const { selectedDepartment } = useBoardAccount();
  const departmentId = selectedDepartment?.department_id;
  const { dateRange } = useTimeFilterContext();
  return useQuery({
    queryKey: ['accounts-partners-by-category', departmentId, dateRange?.startDate, dateRange?.endDate],
    queryFn: () => apiClient.get(
      `/api/vendor-analysis/${encodeURIComponent(PANW)}/partners-by-category`,
      {
        params: {
          department_ids: departmentId,
          ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
          ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        },
      },
    ),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * A tile's three states before it has a figure.
 *
 * Loading and error have to be distinguishable at this size or a tile that
 * failed reads as a tile with nothing in it — which for a spend figure means
 * "this agency buys nothing", the exact misreading the purchase-history widget
 * was fixed for.
 */
function TileState({ q, label }) {
  if (q.isLoading) return <TileFigure value="…" label={label} />;
  if (q.error) return <TileFigure value="—" label={label} sub="unavailable" tone="warn" />;
  return null;
}

/** "1,000-5,000" is three times too long for a tile. "1K-5K" is the same fact. */
function shortRange(range) {
  if (!range) return null;
  const parts = String(range).split(/[-–—]/).map((x) => x.trim());
  const shrink = (x) => {
    const n = Number(String(x).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n) || n === 0) return x;
    if (n >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `${+(n / 1e3).toFixed(n % 1e3 === 0 ? 0 : 1)}K`;
    return String(n);
  };
  return parts.length === 2 ? `${shrink(parts[0])}–${shrink(parts[1])}` : shrink(parts[0]);
}

/** Key numbers — what this agency spends, full stop. */
function StatsTile() {
  const q = useCoverageStats();
  const state = TileState({ q, label: 'Total spend' });
  if (state) return state;
  const row = Array.isArray(q.data) ? q.data[0] : null;
  const total = row ? parseFloat(row.total_spend) || 0 : 0;
  return <TileFigure value={fmtMoney(total)} label="Total spend" tone="accent" />;
}

/** The ceiling this account signs to alone — the number the card is named for. */
function PurchasingAuthorityTile() {
  const q = usePurchasingAuthority();
  const state = TileState({ q, label: 'IT ceiling' });
  if (state) return state;
  const pa = q.data?.purchasingAuthority;
  if (!pa) return <TileFigure value="Unlisted" label="DGS tier" variant="word" tone="warn" />;
  const ceiling = Number(pa.itCeiling);
  if (pa.itCeiling === 'unlimited') {
    return <TileFigure value="No cap" label="IT ceiling" sub={pa.label} variant="word" tone="good" />;
  }
  if (!Number.isFinite(ceiling) || ceiling <= 0) {
    return <TileFigure value={pa.label || 'Unlisted'} label="DGS tier" variant="word" tone="warn" />;
  }
  return <TileFigure value={fmtMoney(ceiling)} label="IT ceiling" sub={pa.label} tone="accent" />;
}

/**
 * Spend trajectory — the DIRECTION, not the level.
 *
 * Ryan's first goal for this board is "are they spending more YoY on IT". The
 * level is already the Key numbers tile; a second tile repeating it would be
 * the same number twice, so this one answers the other half.
 */
function SpendTrajectoryTile() {
  const q = useEvidence();
  const state = TileState({ q, label: 'YoY change' });
  if (state) return state;
  const rows = (q.data?.spendTrend || [])
    .filter((x) => x && x.total != null)
    .sort((a, b) => String(a.fy).localeCompare(String(b.fy)));
  if (rows.length < 2) {
    const only = rows[0];
    return only
      ? <TileFigure value={fmtMoney(only.total)} label={`FY${String(only.fy).slice(-2)} spend`} />
      : <TileFigure value="—" label="Spend" sub="no history" />;
  }
  const latest = rows[rows.length - 1];
  const prior = rows[rows.length - 2];
  const prev = Number(prior.total) || 0;
  if (prev <= 0) {
    return <TileFigure value={fmtMoney(latest.total)} label={`FY${String(latest.fy).slice(-2)} spend`} />;
  }
  const pct = Math.round(((Number(latest.total) || 0) - prev) / prev * 100);
  return (
    <TileFigure
      value={`${pct > 0 ? '+' : ''}${pct}%`}
      label="YoY change"
      sub={fmtMoney(latest.total)}
      // Up is the good direction for a seller: a growing budget is a bigger
      // target, not a risk.
      tone={pct > 0 ? 'good' : pct < 0 ? 'warn' : 'neutral'}
    />
  );
}

/** Security coverage — how many vendors are already inside. */
function SecurityCoverageTile() {
  const q = useSecurityCoverage();
  const state = TileState({ q, label: 'Security vendors' });
  if (state) return state;
  const s = q.data?.summary;
  const vendors = Number(s?.vendorCount) || (q.data?.rows || []).length;
  return (
    <TileFigure
      value={vendors}
      label={vendors === 1 ? 'Security vendor' : 'Security vendors'}
      sub={s?.grandTotalFormatted}
      tone="accent"
    />
  );
}

/** Partners — who carries us in, and how many of them there are. */
function PartnersTile() {
  const q = usePartnersByCategory();
  const state = TileState({ q, label: 'Resellers' });
  if (state) return state;
  const partners = (q.data?.partners || []).filter((p) => p && p.partner);
  if (partners.length === 0) {
    return <TileFigure value="None" label="Resellers" sub="no Palo Alto channel" variant="word" tone="warn" />;
  }
  return (
    <TileFigure
      value={partners.length}
      label={partners.length === 1 ? 'Reseller' : 'Resellers'}
      sub={partners[0].partner}
      tone="accent"
    />
  );
}

/** Identity — how big the agency is, which is the first thing Ryan asked for. */
function IdentityTile() {
  const q = useProfile();
  const state = TileState({ q, label: 'Employees' });
  if (state) return state;
  const p = q.data?.found ? q.data : null;
  const f = p?.facts || {};
  const employees = shortRange(f.employeeCountRange);
  if (employees) return <TileFigure value={employees} label="Employees" />;
  if (f.spend3y != null) return <TileFigure value={fmtMoney(f.spend3y)} label="3-yr spend" tone="accent" />;
  return <TileFigure value="—" label="Employees" sub="not recorded" />;
}

/** Renewals — how much paper is on the clock. */
function RenewalsTile() {
  const q = useRenewals();
  const state = TileState({ q, label: 'Renewals' });
  if (state) return state;
  const rows = q.data?.renewals || [];
  if (rows.length === 0) {
    return <TileFigure value="None" label="Renewals" sub="next 24 months" variant="word" />;
  }
  const value = rows.reduce((sum, r) => sum + (Number(r.contractValue) || 0), 0);
  return (
    <TileFigure
      value={rows.length}
      label="Renewals"
      sub={value > 0 ? `${fmtMoney(value)} · 24mo` : 'next 24 months'}
      tone="accent"
    />
  );
}

/**
 * Procurement summary — how many vendors this agency buys from.
 *
 * Deliberately NOT total spend: Key numbers already shows that, from a
 * different endpoint. Two tiles labelled "Total spend" with figures that can
 * disagree is worse than either of them alone.
 */
function ProcurementSummaryTile() {
  const q = useProcurementSummary();
  const state = TileState({ q, label: 'Vendors' });
  if (state) return state;
  const d = q.data || {};
  const vendors = (d.topVendors || d.top_vendors || []).length;
  return (
    <TileFigure
      value={vendors}
      label={vendors === 1 ? 'Top vendor' : 'Top vendors'}
      sub={d.totalSpend ? fmtMoney(d.totalSpend) : undefined}
    />
  );
}

/** Drivers and objectives — how much is actually written down about this year. */
function BusinessDriversTile() {
  const q = useProfile();
  const state = TileState({ q, label: 'Drivers' });
  if (state) return state;
  const p = q.data?.found ? q.data : null;
  const drivers = (p?.businessDrivers || []).length;
  const objectives = (p?.businessObjectives || []).length;
  if (drivers === 0 && objectives === 0) {
    return <TileFigure value="None" label="Drivers" sub="nothing recorded" variant="word" tone="warn" />;
  }
  return (
    <TileFigure
      value={drivers}
      label={drivers === 1 ? 'Driver' : 'Drivers'}
      sub={objectives > 0 ? `${objectives} objectives` : undefined}
      tone="accent"
    />
  );
}

/** Funding — the one word that changes how a deal gets paid for. */
function FundingTile() {
  const q = useProfile();
  const state = TileState({ q, label: 'Funding' });
  if (state) return state;
  const p = q.data?.found ? q.data : null;
  const f = p?.facts || {};
  if (f.revenueGenerating == null && !p?.fundingModel) {
    return <TileFigure value="Unknown" label="Funding" variant="word" />;
  }
  return (
    <TileFigure
      value={f.revenueGenerating ? 'Revenue' : 'Appropriated'}
      label="Funding"
      variant="word"
      tone={f.revenueGenerating ? 'good' : 'neutral'}
    />
  );
}

/** People — how many are on file for this account. */
function PeopleTile() {
  const q = useWorkspace();
  const state = TileState({ q, label: 'People' });
  if (state) return state;
  const contacts = q.data?.contacts || [];
  return (
    <TileFigure
      value={contacts.length}
      label={contacts.length === 1 ? 'Person on file' : 'People on file'}
      tone={contacts.length === 0 ? 'warn' : 'neutral'}
    />
  );
}

/**
 * Type -> tile, for the widgets that mount a panel. Anything absent falls
 * through to `WidgetTileStub`, which is the honest answer for a card whose
 * numbers live inside a component rather than behind a query key.
 */
const PANEL_TILES = {
  stats: StatsTile,
  'purchasing-authority': PurchasingAuthorityTile,
  'spend-trajectory': SpendTrajectoryTile,
  'security-coverage': SecurityCoverageTile,
  partners: PartnersTile,
  identity: IdentityTile,
  renewals: RenewalsTile,
  'procurement-summary': ProcurementSummaryTile,
  'business-drivers': BusinessDriversTile,
  funding: FundingTile,
  'org-chart': PeopleTile,
};

/**
 * Where this account physically is — the Locations tab, mounted.
 *
 * At tile size the figure is the STAFFED site count, because that is the
 * number a rep quotes ("they have 41 offices"), with the address gap under it
 * when there is one: an office with no address on record is a site the tab
 * cannot put on a map, and that is the thing worth a glance. Staffed only —
 * Caltrans has 3,656 SPI structures and most are barns, so "sites" here means
 * the ones with people in them, the same rule the tab's coverage line follows.
 * When nothing is missing the sub-line says how many are PINNED — matched to a
 * Google listing within 200m of DGS's own coordinate — which is the count that
 * grew from 0 to 2,337 on 2026-09-12.
 *
 * The tile shares the tab's query key, so a board with this widget and a rep
 * who then opens the tab pay for ONE fetch. At any larger size the tab itself
 * is mounted (`compact`) and owns its own fetch, so the widget only asks for
 * data when it is going to draw the figure itself.
 */
function LocationsWidget({ size, onOpen }) {
  const { selectedDepartment } = useBoardAccount();
  const account = accountKey(selectedDepartment);
  const tile = size === 'tile';
  const q = useQuery({
    queryKey: ['accounts-sites', account, false],
    queryFn: () => apiClient.get('/api/sites', { params: { dept: account } }),
    enabled: !!account && tile,
    staleTime: 5 * 60 * 1000,
  });

  if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;

  if (tile) {
    const sites = q.data?.sites || [];
    const staffed = q.data?.coverage?.staffed ?? sites.length;
    const noAddress = sites.filter((s) => !s.addressLine1).length;
    const pinned = sites.filter((s) => s.googlePlaceId).length;
    let sub;
    if (q.data) {
      if (noAddress) sub = `${noAddress} need address`;
      else if (pinned) sub = `${pinned} pinned`;
    }
    return (
      <TileFigure
        value={q.data ? staffed.toLocaleString() : '—'}
        label="SITES"
        sub={sub}
        tone={noAddress ? 'warn' : 'neutral'}
        onClick={onOpen}
        title={q.data
          ? `${staffed} staffed sites · ${pinned} pinned to a verified Google listing`
          : 'Loading locations'}
      />
    );
  }

  return (
    <div className="acb-pad">
      <AccountsLocationsTab selectedDepartment={selectedDepartment} compact />
    </div>
  );
}

/**
 * Widgets that draw their own tile. Everything else gets the mark-and-name
 * placeholder below.
 *
 * A LIST RATHER THAN A FLAG ON EACH COMPONENT, so the answer to "which widgets
 * have a real tile form?" is one thing to read instead of thirty-four. The
 * placeholder is deliberately not silent about being one — see below.
 */
const OWN_TILE = new Set([
  'partner-involvement',
  'locations',
  'tasks',
  'opportunities',
  'meetings',
  'renewal-runway',
  'share-of-wallet',
  'solicitations',
  'coverage-ring',
  'buying-rhythm',
  'competitive',
  'purchase-history',
  'panw-heatmap',
]);

/**
 * Give every widget a tile form: its own if it has one, the placeholder if not.
 *
 * Wrapping the registry rather than editing thirty-four components is what
 * makes the rung safe to land at all. A widget that has not been converted
 * cannot be left rendering a clipped panel by accident — the default is the
 * placeholder, and opting out of it is an explicit line in OWN_TILE.
 */
function withTileForms(registry, metaFor) {
  const out = {};
  for (const [type, Component] of Object.entries(registry)) {
    if (OWN_TILE.has(type)) {
      out[type] = Component;
      continue;
    }
    const PanelTile = PANEL_TILES[type];
    const Tiled = function TiledWidget(props) {
      if (props.size === 'tile') {
        return PanelTile
          ? <PanelTile />
          : <WidgetTileStub type={type} title={metaFor(type)} onOpen={props.onOpen} />;
      }
      return <Component {...props} />;
    };
    Tiled.displayName = `Tiled(${type})`;
    out[type] = Tiled;
  }
  return out;
}

const TEKSTAK_WIDGET_BODIES = {
  identity: fromIntel('identity'),
  stats: fromDash('stats'),
  'purchasing-authority': fromDash('purchasing-authority'),
  // The Locations tab, mounted — and the first widget the catalog took after
  // the id encoding was shortened (2026-09-13). The tab renders `compact`, so
  // the cell shows the sites and nothing else: the review queues and the add
  // form stay on the tab, where there is exactly one copy of each.
  locations: LocationsWidget,

  // Main merged Org chart and Contacts into ONE People tab, so both widgets
  // mount it rather than one of them becoming a placeholder on saved boards.
  // Two widgets, one component, DIFFERENT views. Mounting the whole People tab
  // twice made them identical and overflowed the cell — the roster painted over
  // the legend beneath it.
  // No `view` LOCK, so the switch stays: the merged People widget gives a rep
  // both views where the catalog used to spend two slots on one component's
  // two props. `config.view` only chooses which one opens FIRST, and the
  // v3 -> v4 migration sets it to 'list' for anyone whose board carried the
  // old contacts widget — their board opens exactly as it did, and the chart
  // is now one click away instead of a second card.
  'org-chart': function PeopleWidget({ config }) {
    const { selectedDepartment } = useBoardAccount();
    if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
    return (
      <div className="acb-pad">
        <AccountsPeopleTab
          selectedDepartment={selectedDepartment}
          compact
          initialView={config && config.view === 'list' ? 'list' : null}
        />
      </div>
    );
  },
  /*
   * The directory, not the chart's list mode. It reads the same
   * `/api/workspace` key the People widget does, so the two together cost one
   * fetch and an edit in either reconciles in both.
   */
  'contacts-directory': function ContactsDirectoryWidget() {
    const { selectedDepartment } = useBoardAccount();
    if (!selectedDepartment) return <WidgetStatus state="empty" message="No account selected." />;
    return (
      <div className="acb-pad">
        <AccountsContactsDirectory selectedDepartment={selectedDepartment} />
      </div>
    );
  },
  'contact-inbox': ContactInboxWidget,

  renewals: fromIntel('renewals'),
  'renewal-runway': RenewalRunwayWidget,
  'share-of-wallet': ShareOfWalletWidget,
  'buying-rhythm': BuyingRhythmWidget,
  'spend-trajectory': fromDash('spend'),
  'panw-heatmap': PanwFootprintWidget,
  'partner-involvement': PartnerInvolvementWidget,
  'procurement-summary': fromIntel('procurement-summary'),
  'purchase-history': PurchaseHistoryWidget,
  // The bump IS the competitive question; the tab behind the header stays the
  // full view. Same move as solicitations in W5.
  competitive: DisplacementBumpWidget,
  'security-coverage': fromDash('security'),
  'coverage-ring': CoverageRingWidget,
  partners: fromDash('partners'),
  'tech-map': fromTab(AccountsTechmapTab),
  bcps: fromTab(AccountsBcpsTab),
  // The widget is the glanceable form; the tab behind its header stays the
  // full list (open AND recently closed). Not a second catalog entry — the
  // list was never the better widget, so there is nothing to preserve.
  solicitations: SolicitationClocksWidget,
  'zero-trust': ZeroTrustWidget,
  // The Competitive tab, mounted — so the tab can BE a board without losing
  // the footprint sections no other widget covers.
  'competitive-footprint': fromTab(AccountsCompetitiveTab),

  news: NewsWidget,
  notes: NotesWidget,
  'business-drivers': fromIntel('business-drivers'),
  funding: fromIntel('funding'),
  'value-pyramid': fromTab(AccountsPyramidTab),
  'trap-questions': fromTab(AccountsTrapsTab),

  tasks: TasksWidget,
  opportunities: OpportunitiesWidget,
  meetings: MeetingsWidget,
  // Main's Meetings tab already wraps MeetingHistorySection with the
  // account-id resolution; mount it rather than keeping a second wrapper.
  'plaud-recordings': fromTab(AccountsMeetingsTab),
  'health-score': withAccountId(AccountHealthScore, 'the health score'),
  'proactive-questions': withAccountId(ProactiveQuestions, 'these questions'),
  commitments: withAccountId(CommitmentTracker, 'commitments'),
};

/**
 * The registry the board mounts. Every entry can render at tile size.
 *
 * `ACCOUNT_WIDGET_META` supplies the name for the placeholder rather than a
 * second table of titles here — a widget renamed in the catalog is renamed on
 * its tile, with nothing to keep in step.
 */
export const TEKSTAK_WIDGETS = withTileForms(
  TEKSTAK_WIDGET_BODIES,
  (type) => (ACCOUNT_WIDGET_META[type] ? ACCOUNT_WIDGET_META[type].title : type),
);

/** The undecorated bodies, for tests that want the component itself. */
export { TEKSTAK_WIDGET_BODIES, OWN_TILE, PANEL_TILES };

/** Widgets that show a window; how much of it is a per-instance choice. */
function RowLimitEditor({ config, onChange, onClose }) {
  const limit = Number(config?.limit) > 0 ? Number(config.limit) : 10;
  return (
    <div className="acb-cfg">
      <label className="acb-cfg-row">
        <span>Rows to show</span>
        <input
          type="number"
          min="1"
          max="25"
          value={limit}
          onChange={(e) => onChange({
            ...(config || {}),
            limit: Math.max(1, Math.min(25, Number(e.target.value) || 10)),
          })}
        />
      </label>
      <button type="button" className="acb-cfg-done" onClick={onClose}>Done</button>
    </div>
  );
}

export const TEKSTAK_WIDGET_EDITORS = {
  tasks: RowLimitEditor,
};

export { useQueryClient };
